import React, { Component } from "react";
import Popup from "reactjs-popup";
import { Auth } from "aws-amplify";
import config from "../../config";
import {
  getRepositoriesForChatSearch,
  chatSearchDocuments,
  searchByBlockAndLot,
  aiNaturalLanguageSearch,
  ragAnswer,
  documentSummaries,
  createAiSearchCancellation,
  submitAiSearchFeedback,
} from "../../libs/API";
import "./ChatSearch.css";
import RagEvidenceCard from "./RagEvidenceCard";
import RagAnswer from "./RagAnswer";
import RagMissCard from "./RagMissCard";
import SearchHelpCard from "./SearchHelpCard";
import { blockLotSearchError } from "./searchModes";
import { coverageMessage } from "./reportPresentation";
import SearchSessionReport from "./SearchSessionReport";
import DocumentSummaries, {
  summaryDocumentKey,
  summaryCompleted,
} from "./DocumentSummaries";

// Embedded modes used by the Search screen (search.js). "semantic" shows a ranked document list in the screen's
// shared grid; "assistant" keeps the conversation, grounded answers, summaries and reports inside this component.
export const CHAT_SEARCH_MODE_SEMANTIC = "semantic";
export const CHAT_SEARCH_MODE_ASSISTANT = "assistant";
export const AI_NOT_ENTITLED_ERROR_CODE = "AI_NOT_ENTITLED";

// Who may use AI search while the server's seat gate is switched off (AiEntitlements:Enforce false, the default):
// today's rule, unchanged. Once the gate is on, GET api/Entitlements/me decides instead.
export const isAiAllowListed = (login) =>
  typeof login === "string" &&
  AI_NATURAL_LANGUAGE_SEARCH_DEMO_EMAILS.includes(login.trim().toLowerCase());

const AI_NATURAL_LANGUAGE_SEARCH_DEMO_EMAILS = [
  "oceancity@documentsync.com",
  "lemongroveca@documentsync.com",
  "haddonheights@documentsync.com",
  "demo@documentsync.com",
  "passaiccounty@documentsync.com",
  "documentsyncacademy@foveonics.com",
  "hoboken@documentsync.com",
  "howellremediation@documentsync.com",
  "abhijeet@foveonics.com"
];
// No sample values: records and their fields differ for every customer, so a fixed example fits nobody.
const NO_SESSION_ERROR = "Questions about these results are unavailable. Choose New search to try again.";
const countOf = (n, one, many) => n + " " + (n === "1" ? one : many);
// An older server sentence written without its apostrophe, shown as written elsewhere ("couldnt" -> "couldn't"), and
// the server's search summary ("Evaluated 52 candidates ...: no verified matches were found and retained 27 broader
// related records for review.") in the words the screen uses: records checked, verified and related.
const readableServerText = (text) =>
  typeof text === "string"
    ? text
        .replace(/\b(couldn|didn|doesn|isn|wasn|can)t\b/gi, "$1't")
        .replace(/\bEvaluated (\d[\d,]*) candidates? for /, (m, n) => "I checked " + countOf(n, "record", "records") + " for ")
        .replace(/: no verified matches were found and retained (\d[\d,]*) broader related records? for review\./, (m, b) =>
          ": " + countOf(b, "related record is", "related records are") + " listed for you to check.")
        .replace(/: no verified matches were found\./, ": no matching records were found.")
        .replace(/: found (\d[\d,]*) verified match(?:es)? and retained (\d[\d,]*) broader related records? for review\./, (m, v, b) =>
          ": " + countOf(v, "is a verified match", "are verified matches") + " and " + b + " more " + (b === "1" ? "is" : "are") + " related.")
        .replace(/: found (\d[\d,]*) verified match(?:es)?\./, (m, v) => ": " + countOf(v, "is a verified match", "are verified matches") + ".")
        .replace(/ Showing \d[\d,]* broader results?\./, "")
        .replace(/ Showing \d[\d,]* results with verified matches first\./, " Verified matches are listed first.")
        .replace(/ Showing the top (\d[\d,]*) verified results?\./, (m, n) => " The top " + countOf(n, "verified match is", "verified matches are") + " listed.")
    : text;
const REGULAR_SEARCH_PLACEHOLDER = "Type words, a number or a phrase from the documents";
const AI_DOCUMENT_RESULT_RESPONSE_TYPES = {
  DOCUMENT_RESULTS: true,
};

class ChatSearch extends Component {
  constructor(props) {
    super(props);
    this.state = {
      repositories: [],
      selectedRepositoryId: null,
      allRepositories: true,
      activeSearchTab: "document",
      block: "",
      lot: "",
      blockLotResults: [],
      blockLotResultCount: 0,
      blockLotIsTruncated: false,
      blockLotTemplatesEvaluated: 0,
      blockLotTemplatesSearched: 0,
      blockLotLoading: false,
      blockLotError: "",
      blockLotSubmitted: false,
      blockLotSearchedBlock: "",
      blockLotSearchedLot: "",
      query: "",
      lastParsedSearchText: null,
      messages: [],
      isLoading: false,
      error: "",
      viewerOpen: false,
      viewerFile: null,
      isAiNaturalLanguageSearch: false,
      aiAnswer: "",
      aiDocuments: [],
      aiSuggestedActions: [],
      aiSearchPlan: null,
      aiTotalMatches: 0,
      aiReturnedDocumentCount: 0,
      aiSummarizedDocumentCount: 0,
      aiBroadCandidateCount: 0,
      aiRerankSubmittedCount: 0,
      aiRerankEvaluatedCount: 0,
      aiDisplayedDocumentCount: null,
      aiHasMoreMatches: false,
      aiTotalMatchesIsExact: false,
      aiRequestId: null,
      aiSelectedDocumentCount: null,
      aiSearchMode: "",
      showAiDocuments: false,
      aiDocumentDisplayMode: null,
      aiConversationHistory: [],
      aiChatMessages: [],
      aiLoading: false,
      aiError: null,
      lastAiUserQuery: "",
      aiConversationId: null,
      activeSearchSessionId: null,
      summarySelected: [],
      summaryResponse: null,
      summaryLoading: false,
      summaryError: null,
      ragNeedsNewSearch: false,
      ragEvidence: [],
      ragAnswerResponse: null,
      ragCoverageWarning: null,
      ragMissExplanation: null,
      ragStatus: null,
      ragQuestion: "",
      searchSessionDocumentCount: null,
      aiAvailable: true,
    };
    // Embedded in the Search screen, natural-language search is the only mode: no toggle to click.
    if (props.embedded) {
      this.state.isAiNaturalLanguageSearch = true;
    }
    this.isViewerOpening = false;
    this.chatHistoryRef = React.createRef();
    this.chatInputRef = React.createRef();
    this.aiResultSectionRefs = {};
  }

  componentDidMount() {
    // The Search screen owns the repository/template scope when this component is embedded.
    if (!this.props.embedded) {
      this.loadRepositories();
    }
  }

  // ---- Embedding contract (used by search.js) --------------------------------------------------------------

  isEmbedded = () => Boolean(this.props.embedded);

  getEmbeddedMode = () =>
    this.props.mode === CHAT_SEARCH_MODE_ASSISTANT
      ? CHAT_SEARCH_MODE_ASSISTANT
      : CHAT_SEARCH_MODE_SEMANTIC;

  // Documents render inside this component for the assistant; the semantic mode hands them to the parent grid.
  showsDocumentsInline = () =>
    !this.isEmbedded() ||
    this.props.showDocumentsInline === true ||
    (this.props.showDocumentsInline === undefined &&
      this.getEmbeddedMode() === CHAT_SEARCH_MODE_ASSISTANT);

  // The repository/template scope: from props when embedded, from the repository picker otherwise.
  getScope = (props = this.props, state = this.state) => {
    if (props.embedded) {
      const scope = props.scope || {};
      const templateId =
        scope.templateId !== undefined && scope.templateId !== null && scope.templateId !== ""
          ? Number(scope.templateId)
          : null;
      return {
        repositoryId:
          scope.allRepositories === false && scope.repositoryId
            ? scope.repositoryId
            : null,
        allRepositories: scope.allRepositories !== false || !scope.repositoryId,
        templateId: Number.isFinite(templateId) && templateId > 0 ? templateId : null,
        templateName: scope.templateName || null,
      };
    }
    return {
      repositoryId: state.selectedRepositoryId || null,
      allRepositories: Boolean(state.allRepositories),
      templateId: null,
      templateName: null,
    };
  };

  // Public: run a natural-language search (the Search screen's own query box calls this). With an open result
  // set a plain submit is a follow-up question about those results; `fresh` starts a new search instead, which is
  // what the Semantic Search tab wants every time its Search button is pressed.
  submitQuery = (query, options = {}) => {
    if (!options.fresh || (!this.state.activeSearchSessionId && !this.state.ragNeedsNewSearch)) {
      return this.searchWithAiNaturalLanguage(query);
    }
    this.cancelAiRequest();
    this.setState(
      {
        ...this.resetAiConversation(),
        activeSearchSessionId: null,
        ragNeedsNewSearch: false,
        aiConversationId: null,
      },
      () => this.searchWithAiNaturalLanguage(query),
    );
  };

  // Public: put text into the composer without running it (suggested questions).
  setQuery = (query) =>
    this.setState({ query: query || "" }, () => this.focusChatInput(false));

  // Public: drop the current results and session.
  startNewSearch = () => this.beginNewAiSearch();

  // Public: run one of the latest answer's own actions by type ("SHOW_BROADER_MATCHES", "CHECK_MORE_RESULTS"), exactly
  // as its button in the conversation would. Returns false when the latest answer does not offer it.
  runLatestAction = (actionType) => {
    const wanted = String(actionType || "").toUpperCase();
    const messages = Array.isArray(this.state.aiChatMessages) ? this.state.aiChatMessages : [];
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (!message || message.role !== "assistant") continue;
      const action = (Array.isArray(message.suggestedActions) ? message.suggestedActions : []).find(
        (item) => item && String(item.actionType || item.type || "").toUpperCase() === wanted,
      );
      if (!action) return false;
      this.handleAiSuggestedAction(action, message.id);
      return true;
    }
    return false;
  };

  notifyDocuments = (documents, normalized, page = {}) => {
    if (typeof this.props.onDocuments === "function") {
      this.props.onDocuments(documents, {
        responseType: normalized.responseType || "DOCUMENT_RESULTS",
        verifiedMatchCount: normalized.verifiedMatchCount || 0,
        broaderRelatedCount: normalized.broaderRelatedCount || 0,
        candidatesEvaluated: normalized.candidatesEvaluated || 0,
        rangeCoverage: normalized.rangeCoverage || null,
        searchHelp: normalized.searchHelp || null,
        isLaterPage: Boolean(page.isLaterPage),
        searchSessionId: normalized.searchSessionId || null,
        totalMatches: normalized.totalMatches || 0,
        hasBroaderMatches: Boolean(normalized.hasBroaderMatches),
        broaderMatchCount: normalized.broaderMatchCount || 0,
        hasMoreMatches: Boolean(normalized.hasMoreMatches),
        searchPlan: normalized.searchPlan || null,
        answer: normalized.answer || "",
        requestId: normalized.requestId || null,
        aiAvailable: normalized.aiAvailable !== false,
      });
    }
  };

  // Tells the Search screen when an AI search starts and ends (success or failure) so its own button can show
  // the loading state when it owns the query box.
  notifySearching = (searching) => {
    if (typeof this.props.onSearching === "function") {
      this.props.onSearching(Boolean(searching));
    }
  };

  notifyEntitlementDenied = (body) => {
    if (typeof this.props.onEntitlementDenied === "function") {
      this.props.onEntitlementDenied(body || {});
    }
  };

  // The 403 body the API returns when the tenant or user has no AI subscription (AiEntitlementDeniedResponse).
  getEntitlementDenial = (error) => {
    const response = error && error.response;
    const body = response && response.data;
    if (
      response &&
      response.status === 403 &&
      body &&
      typeof body === "object" &&
      body.errorCode === AI_NOT_ENTITLED_ERROR_CODE
    ) {
      return body;
    }
    return null;
  };

  // The questions asked in this session, oldest first: the opening question(s) and the follow-ups.
  conversationQuestions = (state = this.state) => {
    const asked = (Array.isArray(state.aiChatMessages) ? state.aiChatMessages : [])
      .filter((message) => message && message.role === "user" && message.content)
      .map((message) => String(message.content));
    if (state.ragQuestion && asked[asked.length - 1] !== state.ragQuestion) asked.push(String(state.ragQuestion));
    return asked;
  };

  componentDidUpdate(previousProps, previousState) {
    if (
      typeof this.props.onConversationChange === "function" &&
      previousState &&
      JSON.stringify(this.conversationQuestions(previousState)) !== JSON.stringify(this.conversationQuestions())
    ) {
      this.props.onConversationChange(this.conversationQuestions());
    }
    // Results found on the Semantic Search tab and carried to the AI Assistant: show their documents as a table
    // there, as the assistant does for its own searches, so summaries can start from them.
    if (
      this.props.embedded &&
      previousProps &&
      previousProps.mode !== this.props.mode &&
      this.getEmbeddedMode() === CHAT_SEARCH_MODE_ASSISTANT &&
      Array.isArray(this.state.aiChatMessages) &&
      this.state.aiChatMessages.some(
        (message) =>
          !message.showDocuments && this.canOfferAiDocuments(message),
      )
    ) {
      this.setState({
        aiChatMessages: this.state.aiChatMessages.map((message) =>
          !message.showDocuments && this.canOfferAiDocuments(message)
            ? { ...message, showDocuments: true, documentDisplayMode: "table" }
            : message,
        ),
      });
    }
    if (
      this.props.embedded &&
      typeof this.props.onSessionChange === "function" &&
      previousState &&
      previousState.activeSearchSessionId !== this.state.activeSearchSessionId
    ) {
      this.props.onSessionChange(this.state.activeSearchSessionId);
    }
    if (
      previousState &&
      previousState.activeSearchSessionId !==
        this.state.activeSearchSessionId &&
      (this.state.summarySelected.length ||
        this.state.summaryResponse ||
        this.state.summaryLoading)
    ) {
      this.cancelAiRequest();
      this.setState({
        summarySelected: [],
        summaryResponse: null,
        summaryLoading: false,
        summaryError: null,
      });
    }
    if (
      previousState &&
      (this.state.activeSearchSessionId ||
        this.state.aiLoading ||
        this.state.ragNeedsNewSearch) &&
      this.aiContextKey(previousProps, previousState) !== this.aiContextKey()
    ) {
      this.invalidateAiScope();
    }
    if (
      this.state.isAiNaturalLanguageSearch &&
      !this.canUseAiNaturalLanguageSearch()
    ) {
      this.setState({ isAiNaturalLanguageSearch: false });
    }
  }

  componentWillUnmount() {
    this.aiUnmounted = true;
    this.cancelAiRequest();
  }

  aiContextKey = (props = this.props, state = this.state) => {
    const context = props.props || props.searchProps || {};
    const scope = this.getScope(props, state);
    return JSON.stringify([
      scope.repositoryId,
      !!scope.allRepositories,
      scope.templateId,
      context.clientId,
      context.loggedInUser &&
        (context.loggedInUser.username ||
          context.loggedInUser.email ||
          context.loggedInUser),
      !!state.isAiNaturalLanguageSearch,
      state.activeSearchTab,
      // The embedded mode is left out on purpose: Semantic Search and AI Assistant share one session, so moving
      // between the two tabs must not drop it.
    ]);
  };

  cancelAiRequest = () => {
    this.summaryInFlight = false;
    this.aiRequestGeneration = (this.aiRequestGeneration || 0) + 1;
    if (this.aiCancellation)
      this.aiCancellation.cancel("Search context changed");
    this.aiCancellation = null;
    if (
      !this.aiUnmounted &&
      (this.state.summaryLoading ||
        this.state.summaryResponse ||
        this.state.summaryError)
    ) {
      this.setState({
        summaryLoading: false,
        summaryResponse: null,
        summaryError: null,
      });
    }
  };

  toggleSummarySelection = (key) => {
    const previous = this.state.summaryLoading
      ? null
      : this.state.summaryResponse;
    this.cancelAiRequest();
    this.setState((state) => ({
      summarySelected: state.summarySelected.includes(key)
        ? state.summarySelected.filter((item) => item !== key)
        : [...state.summarySelected, key].slice(0, 4),
      summaryResponse: previous,
      aiLoading: false,
    }));
  };

  summarizeDocuments = async (scope, documents) => {
    const searchSessionId = this.state.activeSearchSessionId;
    if (
      !searchSessionId ||
      this.state.ragNeedsNewSearch ||
      this.summaryInFlight
    )
      return;
    const previous = this.state.summaryResponse;
    // Do not regenerate successful documents when explicitly retrying a failed selection.
    const completed = new Set(
      previous && previous.searchSessionId === searchSessionId
        ? previous.documents.filter(summaryCompleted).map(summaryDocumentKey)
        : [],
    );
    if (scope === "CURRENT_SEARCH_SESSION" && completed.size) {
      documents = this.state.aiDocuments;
      scope = "SELECTED_DOCUMENTS";
    }
    documents = documents.filter((d) => !completed.has(summaryDocumentKey(d)));
    if (scope !== "CURRENT_SEARCH_SESSION" && !documents.length) return;
    this.cancelAiRequest();
    this.summaryInFlight = true;
    const generation = this.aiRequestGeneration;
    const releaseSummary = () => {
      if (generation === this.aiRequestGeneration) this.summaryInFlight = false;
    };
    const contextKey = this.aiContextKey();
    const selection = JSON.stringify(this.state.summarySelected);
    this.aiCancellation = createAiSearchCancellation();
    const isCurrent = () =>
      !this.aiUnmounted &&
      generation === this.aiRequestGeneration &&
      searchSessionId === this.state.activeSearchSessionId &&
      contextKey === this.aiContextKey() &&
      selection === JSON.stringify(this.state.summarySelected);
    this.setState({
      summaryLoading: true,
      summaryResponse: previous,
      summaryError: null,
      aiLoading: false,
    });
    try {
      const response = await documentSummaries(
        { searchSessionId, scope, documents, includeCombinedSummary: false },
        this.aiCancellation.token,
      );
      if (!isCurrent()) {
        releaseSummary();
        return;
      }
      if (
        !response ||
        response.searchSessionId !== searchSessionId ||
        !Array.isArray(response.documents) ||
        !["SUCCEEDED", "DEGRADED", "NO_SUMMARIES"].includes(response.status)
      )
        throw new Error("Invalid summary response");
      const requested = new Set(documents.map(summaryDocumentKey));
      const seen = new Set();
      for (const document of response.documents) {
        const key = summaryDocumentKey(document);
        if (
          !Number.isInteger(document.templateId) ||
          document.templateId <= 0 ||
          !Number.isInteger(document.fileId) ||
          document.fileId <= 0 ||
          seen.has(key) ||
          (scope !== "CURRENT_SEARCH_SESSION" && !requested.has(key)) ||
          typeof document.status !== "string" ||
          !document.coverage ||
          typeof document.coverage.status !== "string" ||
          !Array.isArray(document.findings) ||
          !Array.isArray(document.evidenceReferences) ||
          !Array.isArray(document.missingInformation) ||
          !document.evidenceReferences.every(
            (e) =>
              e &&
              typeof e.evidenceId === "string" &&
              typeof e.quote === "string",
          ) ||
          !document.findings.every(
            (f) =>
              f &&
              typeof f.text === "string" &&
              typeof f.evidenceId === "string",
          ) ||
          !document.missingInformation.every((text) => typeof text === "string")
        )
          throw new Error("Invalid summary presentation");
        seen.add(key);
      }
      const merged = new Map(
        (previous && previous.searchSessionId === searchSessionId
          ? previous.documents
          : []
        ).map((d) => [summaryDocumentKey(d), d]),
      );
      response.documents.forEach((d) => merged.set(summaryDocumentKey(d), d));
      this.setState(
        {
          summaryResponse: { ...response, documents: [...merged.values()] },
          summaryLoading: false,
        },
        releaseSummary,
      );
    } catch (error) {
      if (!isCurrent()) {
        releaseSummary();
        return;
      }
      const body = error.response && error.response.data;
      if (
        body &&
        (body.newSearchRequired ||
          [
            "SESSION_EXPIRED",
            "SESSION_NOT_ACCESSIBLE",
            "EMPTY_AUTHORIZED_SCOPE",
          ].includes(body.status))
      ) {
        this.handleRagAnswerFailure(body, error.response.status);
      }
      this.setState(
        {
          summaryLoading: false,
          summaryError: error.__CANCEL__
            ? null
            : body && body.status === "SELECTION_REQUIRED"
              ? "Select at most four documents to summarize."
              : body && body.status === "SENSITIVE_DENIED"
                ? "Summarizing restricted identifiers is not allowed."
                : "The summary request could not complete. You can try the selected documents again.",
        },
        releaseSummary,
      );
    }
  };

  invalidateAiScope = () => {
    this.cancelAiRequest();
    this.setState({
      activeSearchSessionId: null,
      ragNeedsNewSearch: true,
      ragAnswerResponse: null,
      ragCoverageWarning: null,
      ragMissExplanation: null,
      ragEvidence: [],
      ragStatus: null,
      ragQuestion: "",
      searchSessionDocumentCount: null,
      aiLoading: false,
      aiError: "Your search settings changed. Choose New search to continue.",
    });
  };

  beginNewAiSearch = () => {
    this.cancelAiRequest();
    this.setState(
      {
        ...this.resetAiConversation(),
        activeSearchSessionId: null,
        ragNeedsNewSearch: false,
        ragEvidence: [],
        ragStatus: null,
        aiConversationId: null,
        query: "",
      },
      this.focusChatInput,
    );
  };

  // "Search for: ..." on a no-answer card: a NEW search with the user's own (edited) words, never a question about
  // the current results.
  startSuggestedSearch = (text) => {
    const query = (text || "").trim();
    if (!query) return;
    if (typeof this.props.onSuggestedSearch === "function") {
      this.props.onSuggestedSearch(query);
      return;
    }
    this.cancelAiRequest();
    return new Promise((resolve) =>
      this.setState(
        {
          ...this.resetAiConversation(),
          activeSearchSessionId: null,
          ragNeedsNewSearch: false,
          aiConversationId: null,
          query: "",
        },
        () => resolve(this.searchWithAiNaturalLanguage(query)),
      ),
    );
  };

  focusChatInput = () => {
    if (this.chatInputRef.current) this.chatInputRef.current.focus();
  };

  getSearchSessionDocumentCount = (response) => {
    const count =
      response.searchSessionDocumentCount !== undefined
        ? response.searchSessionDocumentCount
        : response.SearchSessionDocumentCount;
    return typeof count === "number" && Number.isInteger(count) && count >= 0
      ? count
      : null;
  };

  getLoggedInUserEmail = () => {
    const user =
      this.props.searchProps &&
      this.props.searchProps.loggedInUser !== undefined
        ? this.props.searchProps.loggedInUser
        : this.props.props
          ? this.props.props.loggedInUser
          : null;

    if (typeof user === "string") {
      return user;
    }

    if (user && typeof user === "object") {
      const email =
        user.email ||
        user.Email ||
        (user.attributes && user.attributes.email) ||
        "";
      return typeof email === "string" ? email : "";
    }

    return "";
  };

  // Whether natural-language (AI) search is offered. The Search screen passes aiEnabled from
  // GET api/Entitlements/me; the API enforces the same entitlement, so a stale or missing flag only changes what
  // is shown, never what is allowed.
  canUseAiNaturalLanguageSearch = () => {
    if (typeof this.props.aiEnabled === "boolean") return this.props.aiEnabled;
    return isAiAllowListed(this.getLoggedInUserEmail());
  };
  async loadRepositories() {
    try {
      const repositories = await getRepositoriesForChatSearch();
      this.setState({
        repositories: Array.isArray(repositories) ? repositories : [],
      });
    } catch (error) {
      console.error("ChatSearch repositories fetch failed", error);
      this.setState({ repositories: [] });
    }
  }

  formatTemplateName(templateName) {
    if (!templateName) return "-";
    let s = String(templateName);
    // remove leading 'inx' prefix (case-insensitive)
    s = s.replace(/^inx/i, "");
    // remove any leading underscores or spaces left behind
    s = s.replace(/^[_\s]+/, "");
    // remove trailing numeric suffix (with optional preceding underscore/dash)
    s = s.replace(/[_-]?\d+$/i, "");
    // remove any trailing underscores or spaces
    s = s.replace(/[_\s]+$/, "");
    // replace underscores with spaces
    s = s.replace(/_/g, " ");
    // collapse multiple spaces
    s = s.replace(/\s+/g, " ");
    s = s.trim();
    return s.length > 0 ? s : "-";
  }

  getRepositoryLabel(repository) {
    if (!repository) return "Repository";
    return (
      repository.repositoryName ||
      repository.name ||
      repository.repoName ||
      repository.folderName ||
      repository.owner ||
      `Repository ${repository.repositoryId || repository.id || ""}`
    );
  }

  handleRepositoryChange = (event) => {
    const value = event.target.value;
    if (value === "all") {
      this.setState({ selectedRepositoryId: null, allRepositories: true });
    } else {
      this.setState({ selectedRepositoryId: value, allRepositories: false });
    }
  };

  handleSearchTabChange = (activeSearchTab) => {
    this.setState({ activeSearchTab });
  };

  handleBlockChange = (event) => {
    this.setState({ block: event.target.value, blockLotError: "" });
  };

  handleLotChange = (event) => {
    this.setState({ lot: event.target.value, blockLotError: "" });
  };

  parseQuery = (raw) => {
    if (!raw) return "";
    const text = raw.trim();
    const patterns = [
      /^search\s+for\s+/i,
      /^search\s+/i,
      /^find\s+/i,
      /^show\s+me\s+/i,
      /^show\s+/i,
    ];
    let parsed = text;
    for (let i = 0; i < patterns.length; i++) {
      if (patterns[i].test(parsed)) {
        parsed = parsed.replace(patterns[i], "").trim();
        break;
      }
    }
    return parsed;
  };

  handleQueryChange = (event) => {
    this.setState({ query: event.target.value });
  };

  addMessage = (message) => {
    this.setState(
      (prevState) => ({
        messages: prevState.messages.concat({
          ...message,
          id: `${Date.now()}-${Math.random()}`,
        }),
      }),
      this.scrollToBottom,
    );
  };

  normalizeChatSearchResponse = (data) => {
    const value = data && typeof data === "object" ? data : {};
    const results = Array.isArray(data)
      ? data
      : value.results ||
        value.Results ||
        value.searchResults ||
        value.SearchResults ||
        [];

    const parsedTextSearch =
      value.parsedTextSearch ||
      value.ParsedTextSearch ||
      value.searchText ||
      value.SearchText ||
      null;

    return {
      results: Array.isArray(results) ? results : [],
      parsedTextSearch,
    };
  };

  normalizeBlockLotResult = (item) => {
    const value = item && typeof item === "object" ? item : {};
    return {
      fileId:
        value.fileId !== undefined && value.fileId !== null
          ? value.fileId
          : value.FileId,
      fileName: value.fileName || value.FileName || "",
      repositoryName: value.repositoryName || value.RepositoryName || "",
      templateName: value.templateName || value.TemplateName || "",
    };
  };

  normalizeBlockLotSearchResponse = (data) => {
    const value = data && typeof data === "object" ? data : {};
    const rawResults =
      value.results !== undefined ? value.results : value.Results;
    const results = Array.isArray(rawResults)
      ? rawResults.map(this.normalizeBlockLotResult)
      : [];
    const rawResultCount =
      value.resultCount !== undefined ? value.resultCount : value.ResultCount;
    const rawIsTruncated =
      value.isTruncated !== undefined ? value.isTruncated : value.IsTruncated;
    const rawTemplatesEvaluated =
      value.templatesEvaluated !== undefined
        ? value.templatesEvaluated
        : value.TemplatesEvaluated;
    const rawTemplatesSearched =
      value.templatesSearched !== undefined
        ? value.templatesSearched
        : value.TemplatesSearched;

    return {
      results,
      resultCount:
        rawResultCount !== undefined && rawResultCount !== null
          ? Number(rawResultCount) || 0
          : results.length,
      isTruncated: rawIsTruncated === true,
      templatesEvaluated: Number(rawTemplatesEvaluated) || 0,
      templatesSearched: Number(rawTemplatesSearched) || 0,
    };
  };

  normalizeAiDocument = (document) => {
    const value = document || {};
    return {
      fileId:
        value.fileId !== undefined && value.fileId !== null
          ? value.fileId
          : value.FileId,
      fileName: value.fileName || value.FileName || "",
      repositoryName: value.repositoryName || value.RepositoryName || "",
      templateName: value.templateName || value.TemplateName || "",
      templateId:
        value.templateId !== undefined && value.templateId !== null
          ? value.templateId
          : value.TemplateId,
      candidateOrdinal:
        value.candidateOrdinal !== undefined && value.candidateOrdinal !== null
          ? value.candidateOrdinal
          : value.CandidateOrdinal,
      repositoryId:
        value.repositoryId !== undefined && value.repositoryId !== null
          ? value.repositoryId
          : value.RepositoryId,
      score: Number(value.score !== undefined ? value.score : value.Score) || 0,
      // Page count and document date as the server read them for the grids (absent when unknown).
      pageCount: Number.isInteger(value.pageCount) && value.pageCount > 0 ? value.pageCount : null,
      documentDate: typeof value.documentDate === "string" ? value.documentDate : null,
      // The record's redaction flag (tblFiles.HasAnnotations); null when the server could not read it.
      hasAnnotations:
        typeof value.hasAnnotations === "boolean" ? value.hasAnnotations : null,
      qualificationTier:
        value.qualificationTier ||
        value.QualificationTier ||
        value.verificationTier ||
        "",
      viewerUrl: value.viewerUrl || value.ViewerUrl || "",
      highlights: Array.isArray(value.highlights)
        ? value.highlights
        : Array.isArray(value.Highlights)
          ? value.Highlights
          : [],
      reason:
        value.matchReason ||
        value.MatchReason ||
        value.reason ||
        value.Reason ||
        "",
      matchedRequiredGroups: Array.isArray(value.matchedRequiredGroups)
        ? value.matchedRequiredGroups
        : Array.isArray(value.MatchedRequiredGroups)
          ? value.MatchedRequiredGroups
          : [],
      matchedQueryNames: Array.isArray(value.matchedQueryNames)
        ? value.matchedQueryNames
        : Array.isArray(value.MatchedQueryNames)
          ? value.MatchedQueryNames
          : [],
    };
  };

  normalizeSuggestedActions = (actions) => {
    const actionOverrides = {
      "show as table": { label: "Show as table", actionType: "SHOW_TABLE" },
      "show file numbers": {
        label: "Show file names only",
        actionType: "SHOW_FILE_NAMES",
      },
      "show file numbers only": {
        label: "Show file names only",
        actionType: "SHOW_FILE_NAMES",
      },
      "show file names": {
        label: "Show file names only",
        actionType: "SHOW_FILE_NAMES",
      },
      "show file names only": {
        label: "Show file names only",
        actionType: "SHOW_FILE_NAMES",
      },
      "show viewer links": {
        label: "Show viewer links",
        actionType: "SHOW_LINKS",
      },
      "review broader matches": {
        label: "Review broader matches",
        actionType: "SHOW_BROADER_MATCHES",
      },
      "review broader results": {
        label: "Review broader matches",
        actionType: "SHOW_BROADER_MATCHES",
      },
      "broader matches": {
        label: "Review broader matches",
        actionType: "SHOW_BROADER_MATCHES",
      },
      "show broader matches": {
        label: "Review broader matches",
        actionType: "SHOW_BROADER_MATCHES",
      },
      "show related results": {
        label: "Review broader matches",
        actionType: "SHOW_BROADER_MATCHES",
      },
      "revise criteria": {
        label: "Revise criteria",
        actionType: "REVISE_CRITERIA",
      },
      "change the search": {
        label: "Revise criteria",
        actionType: "REVISE_CRITERIA",
      },
      "revise search": {
        label: "Revise criteria",
        actionType: "REVISE_CRITERIA",
      },
      "edit search": {
        label: "Revise criteria",
        actionType: "REVISE_CRITERIA",
      },
      "clarify search": {
        label: "Clarify search",
        actionType: "CLARIFY_SEARCH",
      },
      "add more details": {
        label: "Clarify search",
        actionType: "CLARIFY_SEARCH",
      },
      "add details": { label: "Clarify search", actionType: "CLARIFY_SEARCH" },
      clarify: { label: "Clarify search", actionType: "CLARIFY_SEARCH" },
    };
    const actionTypeLabels = {
      SHOW_BROADER_MATCHES: "Review broader matches",
      REVISE_CRITERIA: "Revise criteria",
      CLARIFY_SEARCH: "Clarify search",
      CHECK_MORE_RESULTS: "Check more results",
      SHOW_TABLE: "Show as table",
      SHOW_FILE_NAMES: "Show file names only",
      SHOW_LINKS: "Show viewer links",
    };
    const blockedLabels = {
      "download all": true,
      "download all matching documents": true,
    };
    const uniqueActions = [];
    const seen = {};

    if (!Array.isArray(actions)) {
      return uniqueActions;
    }

    actions.forEach((action) => {
      const isObject = action && typeof action === "object";
      const rawAction = isObject
        ? String(action.label || action.Label || "").trim()
        : String(action || "").trim();
      const rawActionType = isObject
        ? String(action.actionType || action.ActionType || "").trim()
        : "";
      const followUpQuery = isObject
        ? String(action.followUpQuery || action.FollowUpQuery || "").trim()
        : "";
      const normalized = rawAction.toLowerCase();
      const normalizedType = rawActionType.toUpperCase();
      const actionInfo = actionOverrides[normalized] || null;
      const actionType =
        normalizedType ||
        (actionInfo && actionInfo.actionType) ||
        (isObject ? "UNKNOWN" : "SUBMIT_FOLLOW_UP");
      const displayAction =
        (actionInfo && actionInfo.label) ||
        rawAction ||
        actionTypeLabels[actionType] ||
        (actionType === "SUBMIT_FOLLOW_UP" ? followUpQuery : "");
      const internalLabel =
        /lexical|vector|embedding|cohere|nova|candidate fusion/i.test(
          displayAction,
        );

      if (
        displayAction &&
        actionType &&
        !blockedLabels[normalized] &&
        !internalLabel &&
        !seen[displayAction.toLowerCase()]
      ) {
        seen[displayAction.toLowerCase()] = true;
        uniqueActions.push({
          label: displayAction,
          actionType,
          followUpQuery,
          value: rawAction,
        });
      }
    });

    return uniqueActions;
  };

  isJsonLikeAiAnswer = (answer) => {
    const value = String(answer || "").trim();
    const lower = value.toLowerCase();

    if (!value) return false;
    if (value.indexOf("{") === 0 || value.indexOf("[") === 0) return true;
    if (lower.indexOf("```json") === 0) return true;
    if (lower.indexOf('"documentreasons"') >= 0) return true;
    if (lower.indexOf('"selectedindices"') >= 0) return true;
    if (
      lower.indexOf('"suggestedactions"') >= 0 &&
      (value.indexOf("[") < 0 || value.indexOf("]") < 0)
    ) {
      return true;
    }
    if (
      (value.indexOf("{") >= 0 || value.indexOf("[") >= 0) &&
      (value.indexOf("}") < 0 || value.indexOf("]") < 0)
    ) {
      return true;
    }

    return false;
  };

  getSafeAiAnswer = (answer, requestId) => {
    if (this.isJsonLikeAiAnswer(answer)) {
      console.warn("AI ANSWER REJECTED AS JSON-LIKE OUTPUT", requestId);
      return (
        "I found relevant documents for the requested criteria. The results are " +
        "available below, but the conversational summary could not be displayed."
      );
    }

    return answer || "";
  };

  normalizeBedrockAiSearchResponse = (data) => {
    const response = data && data.data ? data.data : data || {};
    const rawTotalMatches =
      response.totalMatches !== undefined
        ? response.totalMatches
        : response.TotalMatches;
    const rawReturnedDocumentCount =
      response.returnedDocumentCount !== undefined
        ? response.returnedDocumentCount
        : response.ReturnedDocumentCount;
    const rawSummarizedDocumentCount =
      response.summarizedDocumentCount !== undefined
        ? response.summarizedDocumentCount
        : response.SummarizedDocumentCount;
    const rawSelectedDocumentCount =
      response.selectedDocumentCount !== undefined
        ? response.selectedDocumentCount
        : response.SelectedDocumentCount;
    const rawBroadCandidateCount =
      response.broadCandidateCount !== undefined
        ? response.broadCandidateCount
        : response.BroadCandidateCount;
    const rawRerankEvaluatedCount =
      response.rerankEvaluatedCount !== undefined
        ? response.rerankEvaluatedCount
        : response.RerankEvaluatedCount;
    const rawRerankSubmittedCount =
      response.rerankSubmittedCount !== undefined
        ? response.rerankSubmittedCount
        : response.RerankSubmittedCount;
    const rawDisplayedDocumentCount =
      response.displayedDocumentCount !== undefined
        ? response.displayedDocumentCount
        : response.DisplayedDocumentCount;
    const rawBroaderMatchCount =
      response.broaderMatchCount !== undefined
        ? response.broaderMatchCount
        : response.BroaderMatchCount;
    const requestId = response.requestId || response.RequestId || null;
    const documents =
      response.documents ||
      response.Documents ||
      response.results ||
      response.Results ||
      [];
    const responseType =
      response.responseType || response.ResponseType || "DOCUMENT_RESULTS";
    const answer = this.getSafeAiAnswer(
      response.assistantAnswer ||
        response.AssistantAnswer ||
        response.answer ||
        response.Answer ||
        "",
      requestId,
    );

    return {
      searchSessionId:
        response.searchSessionId || response.SearchSessionId || null,
      searchSessionDocumentCount: this.getSearchSessionDocumentCount(response),
      answer,
      assistantAnswer: answer,
      responseType,
      shouldShowDocuments:
        response.shouldShowDocuments !== undefined
          ? response.shouldShowDocuments === true
          : response.ShouldShowDocuments === true,
      hasBroaderMatches:
        response.hasBroaderMatches !== undefined
          ? Boolean(response.hasBroaderMatches)
          : response.HasBroaderMatches !== undefined
            ? Boolean(response.HasBroaderMatches)
            : false,
      broaderMatchCount:
        rawBroaderMatchCount !== undefined && rawBroaderMatchCount !== null
          ? Number(rawBroaderMatchCount) || 0
          : 0,
      totalMatches:
        rawTotalMatches !== undefined && rawTotalMatches !== null
          ? Number(rawTotalMatches) || 0
          : Array.isArray(documents)
            ? documents.length
            : 0,
      returnedDocumentCount:
        rawReturnedDocumentCount !== undefined &&
        rawReturnedDocumentCount !== null
          ? Number(rawReturnedDocumentCount) || 0
          : Array.isArray(documents)
            ? documents.length
            : 0,
      summarizedDocumentCount:
        rawSummarizedDocumentCount !== undefined &&
        rawSummarizedDocumentCount !== null
          ? Number(rawSummarizedDocumentCount) || 0
          : 0,
      hasMoreMatches:
        response.hasMoreMatches !== undefined
          ? Boolean(response.hasMoreMatches)
          : response.HasMoreMatches !== undefined
            ? Boolean(response.HasMoreMatches)
            : false,
      totalMatchesIsExact:
        response.totalMatchesIsExact !== undefined
          ? Boolean(response.totalMatchesIsExact)
          : response.TotalMatchesIsExact !== undefined
            ? Boolean(response.TotalMatchesIsExact)
            : false,
      requestId,
      selectedDocumentCount:
        rawSelectedDocumentCount !== undefined &&
        rawSelectedDocumentCount !== null
          ? Number(rawSelectedDocumentCount) || 0
          : null,
      broadCandidateCount:
        rawBroadCandidateCount !== undefined && rawBroadCandidateCount !== null
          ? Number(rawBroadCandidateCount) || 0
          : 0,
      rerankEvaluatedCount:
        rawRerankEvaluatedCount !== undefined &&
        rawRerankEvaluatedCount !== null
          ? Number(rawRerankEvaluatedCount) || 0
          : 0,
      rerankSubmittedCount:
        rawRerankSubmittedCount !== undefined &&
        rawRerankSubmittedCount !== null
          ? Number(rawRerankSubmittedCount) || 0
          : 0,
      displayedDocumentCount:
        rawDisplayedDocumentCount !== undefined &&
        rawDisplayedDocumentCount !== null
          ? Number(rawDisplayedDocumentCount) || 0
          : null,
      vectorSearchApplied:
        response.vectorSearchApplied !== undefined
          ? Boolean(response.vectorSearchApplied)
          : response.VectorSearchApplied !== undefined
            ? Boolean(response.VectorSearchApplied)
            : false,
      vectorEmbeddingModelId:
        response.vectorEmbeddingModelId ||
        response.VectorEmbeddingModelId ||
        "",
      lexicalCandidateCount:
        Number(
          response.lexicalCandidateCount !== undefined
            ? response.lexicalCandidateCount
            : response.LexicalCandidateCount,
        ) || 0,
      vectorChunkCandidateCount:
        Number(
          response.vectorChunkCandidateCount !== undefined
            ? response.vectorChunkCandidateCount
            : response.VectorChunkCandidateCount,
        ) || 0,
      vectorDocumentCandidateCount:
        Number(
          response.vectorDocumentCandidateCount !== undefined
            ? response.vectorDocumentCandidateCount
            : response.VectorDocumentCandidateCount,
        ) || 0,
      hybridCandidateCount:
        Number(
          response.hybridCandidateCount !== undefined
            ? response.hybridCandidateCount
            : response.HybridCandidateCount,
        ) || 0,
      semanticRerankApplied:
        response.semanticRerankApplied !== undefined
          ? Boolean(response.semanticRerankApplied)
          : response.SemanticRerankApplied !== undefined
            ? Boolean(response.SemanticRerankApplied)
            : false,
      semanticRerankModelId:
        response.semanticRerankModelId || response.SemanticRerankModelId || "",
      semanticRerankCandidateCount:
        Number(
          response.semanticRerankCandidateCount !== undefined
            ? response.semanticRerankCandidateCount
            : response.SemanticRerankCandidateCount,
        ) || 0,
      finalSelectionCandidateCount:
        Number(
          response.finalSelectionCandidateCount !== undefined
            ? response.finalSelectionCandidateCount
            : response.FinalSelectionCandidateCount,
        ) || 0,
      searchMode: response.searchMode || response.SearchMode || "",
      verifiedMatchCount:
        Number(
          response.verifiedMatchCount !== undefined
            ? response.verifiedMatchCount
            : response.VerifiedMatchCount,
        ) || 0,
      broaderRelatedCount:
        Number(
          response.broaderRelatedCount !== undefined
            ? response.broaderRelatedCount
            : response.BroaderRelatedCount,
        ) || 0,
      candidatesEvaluated:
        Number(
          response.candidatesEvaluated !== undefined
            ? response.candidatesEvaluated
            : response.CandidatesEvaluated,
        ) || 0,
      rangeCoverage: response.rangeCoverage || response.RangeCoverage || null,
      searchHelp: response.searchHelp || response.SearchHelp || null,
      openSearchCandidateTotal:
        Number(
          response.openSearchCandidateTotal !== undefined
            ? response.openSearchCandidateTotal
            : response.OpenSearchCandidateTotal,
        ) || 0,
      documents,
      suggestedActions: this.normalizeSuggestedActions(
        response.suggestedActions || response.SuggestedActions || [],
      ),
      searchPlan: response.searchPlan || response.SearchPlan || null,
      aiAvailable:
        response.aiAvailable !== undefined
          ? Boolean(response.aiAvailable)
          : response.AiAvailable !== undefined
            ? Boolean(response.AiAvailable)
            : response.AIAvailable !== undefined
              ? Boolean(response.AIAvailable)
              : true,
      errorMessage: response.errorMessage || response.ErrorMessage || null,
      conversationId:
        response.conversationId ||
        response.ConversationId ||
        response.aiConversationId ||
        response.AiConversationId ||
        null,
      // "Check more results": the token for the next page of the same search, and this page's number (2 on).
      continuationToken:
        response.continuationToken || response.ContinuationToken || null,
      continuationPage:
        response.continuationPage || response.ContinuationPage || null,
    };
  };

  getLimitedAiConversationHistory = () => {
    const history = Array.isArray(this.state.aiConversationHistory)
      ? this.state.aiConversationHistory
      : [];

    return history.slice(-6).map((item) => ({
      role: item.role,
      content:
        typeof item.content === "string" ? item.content.substring(0, 2000) : "",
    }));
  };

  createAiMessageId = () => {
    return `${Date.now()}-${Math.random()}`;
  };

  createAiUserMessage = (content) => ({
    id: this.createAiMessageId(),
    role: "user",
    content: content,
    createdAt: Date.now(),
    responseType: null,
    assistantAnswer: "",
    originalUserQuery: content,
    requestId: null,
    documentsAllowed: false,
    shouldShowDocuments: false,
    hasBroaderMatches: false,
    broaderMatchCount: 0,
    totalMatches: 0,
    returnedDocumentCount: 0,
    summarizedDocumentCount: 0,
    selectedDocumentCount: null,
    broadCandidateCount: 0,
    rerankSubmittedCount: 0,
    rerankEvaluatedCount: 0,
    displayedDocumentCount: null,
    hasMoreMatches: false,
    totalMatchesIsExact: false,
    vectorSearchApplied: false,
    lexicalCandidateCount: 0,
    vectorChunkCandidateCount: 0,
    vectorDocumentCandidateCount: 0,
    hybridCandidateCount: 0,
    semanticRerankApplied: false,
    semanticRerankCandidateCount: 0,
    finalSelectionCandidateCount: 0,
    searchMode: "",
    openSearchCandidateTotal: 0,
    documents: [],
    suggestedActions: [],
    searchPlan: null,
    aiAvailable: true,
    errorMessage: null,
    showDocuments: false,
    documentDisplayMode: null,
    feedbackStatus: null,
    feedbackError: null,
    feedbackSubmitting: false,
    showNotHelpfulFeedback: false,
    feedbackReasonCode: "NotHelpful",
    feedbackComment: "",
  });

  createAiAssistantMessage = (
    content,
    normalized,
    documents,
    originalUserQuery,
  ) => ({
    id: this.createAiMessageId(),
    role: "assistant",
    content: content,
    createdAt: Date.now(),
    responseType: normalized.responseType || "DOCUMENT_RESULTS",
    assistantAnswer: normalized.assistantAnswer || content,
    originalUserQuery: originalUserQuery || "",
    requestId: normalized.requestId || null,
    documentsAllowed:
      normalized.shouldShowDocuments === true &&
      AI_DOCUMENT_RESULT_RESPONSE_TYPES[
        String(normalized.responseType || "DOCUMENT_RESULTS").toUpperCase()
      ] === true,
    shouldShowDocuments: normalized.shouldShowDocuments === true,
    hasBroaderMatches: Boolean(normalized.hasBroaderMatches),
    broaderMatchCount: normalized.broaderMatchCount || 0,
    totalMatches: normalized.totalMatches || 0,
    returnedDocumentCount: normalized.returnedDocumentCount || 0,
    summarizedDocumentCount: normalized.summarizedDocumentCount || 0,
    selectedDocumentCount:
      normalized.selectedDocumentCount !== undefined
        ? normalized.selectedDocumentCount
        : null,
    broadCandidateCount: normalized.broadCandidateCount || 0,
    rerankSubmittedCount: normalized.rerankSubmittedCount || 0,
    rerankEvaluatedCount: normalized.rerankEvaluatedCount || 0,
    displayedDocumentCount:
      normalized.displayedDocumentCount !== undefined
        ? normalized.displayedDocumentCount
        : null,
    hasMoreMatches: Boolean(normalized.hasMoreMatches),
    searchHelp: normalized.searchHelp || null,
    totalMatchesIsExact: Boolean(normalized.totalMatchesIsExact),
    vectorSearchApplied: Boolean(normalized.vectorSearchApplied),
    lexicalCandidateCount: normalized.lexicalCandidateCount || 0,
    vectorChunkCandidateCount: normalized.vectorChunkCandidateCount || 0,
    vectorDocumentCandidateCount: normalized.vectorDocumentCandidateCount || 0,
    hybridCandidateCount: normalized.hybridCandidateCount || 0,
    semanticRerankApplied: Boolean(normalized.semanticRerankApplied),
    semanticRerankCandidateCount: normalized.semanticRerankCandidateCount || 0,
    finalSelectionCandidateCount: normalized.finalSelectionCandidateCount || 0,
    searchMode: normalized.searchMode || "",
    openSearchCandidateTotal: normalized.openSearchCandidateTotal || 0,
    documents: Array.isArray(documents) ? documents : [],
    suggestedActions: this.normalizeSuggestedActions(
      normalized.suggestedActions,
    ),
    searchPlan: normalized.searchPlan || null,
    aiAvailable: normalized.aiAvailable !== false,
    errorMessage: normalized.errorMessage || null,
    continuationToken: normalized.continuationToken || null,
    continuationPage: normalized.continuationPage || null,
    showDocuments: false,
    documentDisplayMode: null,
    feedbackStatus: null,
    feedbackError: null,
    feedbackSubmitting: false,
    showNotHelpfulFeedback: false,
    feedbackReasonCode: "NotHelpful",
    feedbackComment: "",
  });

  createAiErrorMessage = (content, requestId) => ({
    id: this.createAiMessageId(),
    role: "assistant",
    content: content,
    createdAt: Date.now(),
    responseType: "GENERAL_MESSAGE",
    assistantAnswer: content,
    originalUserQuery: "",
    requestId: requestId || null,
    documentsAllowed: false,
    shouldShowDocuments: false,
    hasBroaderMatches: false,
    broaderMatchCount: 0,
    totalMatches: 0,
    returnedDocumentCount: 0,
    summarizedDocumentCount: 0,
    selectedDocumentCount: null,
    broadCandidateCount: 0,
    rerankSubmittedCount: 0,
    rerankEvaluatedCount: 0,
    displayedDocumentCount: null,
    hasMoreMatches: false,
    totalMatchesIsExact: false,
    vectorSearchApplied: false,
    lexicalCandidateCount: 0,
    vectorChunkCandidateCount: 0,
    vectorDocumentCandidateCount: 0,
    hybridCandidateCount: 0,
    semanticRerankApplied: false,
    semanticRerankCandidateCount: 0,
    finalSelectionCandidateCount: 0,
    searchMode: "",
    openSearchCandidateTotal: 0,
    documents: [],
    suggestedActions: [],
    searchPlan: null,
    aiAvailable: false,
    errorMessage: content,
    showDocuments: false,
    documentDisplayMode: null,
    feedbackStatus: null,
    feedbackError: null,
    feedbackSubmitting: false,
    showNotHelpfulFeedback: false,
    feedbackReasonCode: "NotHelpful",
    feedbackComment: "",
  });

  createAiLocalAssistantMessage = (content) => ({
    ...this.createAiErrorMessage(content, null),
    aiAvailable: true,
    errorMessage: null,
    feedbackStatus: null,
  });

  getAiStatusText = (message) => {
    if (!this.canOfferAiDocuments(message)) {
      return "";
    }

    const documents = Array.isArray(message.documents) ? message.documents : [];
    const documentCount = documents.length;
    const rerankEvaluatedCount = Number(message.rerankEvaluatedCount) || 0;

    if (documentCount === 0) {
      return "";
    }

    if (message.semanticRerankApplied) {
      return `AI reviewed ${rerankEvaluatedCount} candidate results and is showing the top ${documentCount} ranked results.`;
    }

    return `Showing the top ${documentCount} OpenSearch results.`;
  };

  isDocumentResultResponse = (message) => {
    return Boolean(
      message &&
      AI_DOCUMENT_RESULT_RESPONSE_TYPES[
        String(message.responseType || "").toUpperCase()
      ],
    );
  };

  canOfferAiDocuments = (message) => {
    return (
      this.isDocumentResultResponse(message) &&
      message.shouldShowDocuments === true &&
      message.documentsAllowed === true &&
      Array.isArray(message.documents) &&
      message.documents.length > 0
    );
  };

  canShowAiDocuments = (message) => {
    return (
      this.canOfferAiDocuments(message) &&
      message.documentDisplayMode &&
      message.documentDisplayMode !== "hidden"
    );
  };

  canShowAiSuggestedActions = (message) => {
    const responseType = String(
      message && message.responseType ? message.responseType : "",
    ).toUpperCase();
    return (
      responseType === "DOCUMENT_RESULTS" ||
      responseType === "CLARIFICATION" ||
      responseType === "NO_EXACT_MATCH_WITH_BROADER_RESULTS"
    );
  };

  validateAiDocumentCounts = (normalized, documents) => {
    const documentCount = Array.isArray(documents) ? documents.length : 0;
    const displayedDocumentCount = normalized.displayedDocumentCount;
    const hasDisplayedCount =
      displayedDocumentCount !== undefined && displayedDocumentCount !== null;

    if (hasDisplayedCount && Number(displayedDocumentCount) !== documentCount) {
      console.warn("AI DOCUMENT COUNT MISMATCH", {
        requestId: normalized.requestId || null,
        displayedDocumentCount: hasDisplayedCount
          ? Number(displayedDocumentCount)
          : null,
        documentsLength: documentCount,
      });
    }
  };

  getLatestAssistantMessageWithDocuments = () => {
    const messages = Array.isArray(this.state.aiChatMessages)
      ? this.state.aiChatMessages
      : [];

    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (message.role === "assistant" && this.canOfferAiDocuments(message)) {
        return message;
      }
    }

    return null;
  };

  updateAiMessageDocumentDisplay = (messageId, displayMode) => {
    this.setState(
      (previousState) => ({
        aiChatMessages: (previousState.aiChatMessages || []).map((message) =>
          message.id === messageId
            ? {
                ...message,
                showDocuments: true,
                documentDisplayMode: displayMode,
              }
            : message,
        ),
      }),
      () => this.scrollToAiResultSection(messageId),
    );
  };

  updateAiMessageFeedbackState = (messageId, changes, callback) => {
    this.setState(
      (previousState) => ({
        aiChatMessages: (previousState.aiChatMessages || []).map((message) =>
          message.id === messageId ? { ...message, ...changes } : message,
        ),
      }),
      callback,
    );
  };

  focusChatInput = (selectText) => {
    const input = this.chatInputRef && this.chatInputRef.current;
    if (!input) return;
    input.focus();
    if (selectText && typeof input.select === "function") {
      input.select();
    }
  };

  appendLocalAiAssistantMessage = (content) => {
    this.setState(
      (previousState) => ({
        aiChatMessages: (previousState.aiChatMessages || []).concat(
          this.createAiLocalAssistantMessage(content),
        ),
      }),
      this.scrollToBottom,
    );
  };

  handleReviewBroaderMatches = (message, action) => {
    if (!message || !message.hasBroaderMatches || !message.requestId) {
      this.appendLocalAiAssistantMessage(
        "Please restate the search before reviewing broader matches.",
      );
      return;
    }

    this.searchWithAiNaturalLanguage(
      action && action.followUpQuery
        ? action.followUpQuery
        : "Show the broader matches.",
      {
        previousSearchPlan:
          message.searchPlan || this.state.aiSearchPlan || null,
      },
    );
  };

  // "Check more results": the next page of the same search. The server takes the token only with the request it
  // was issued for, so that request is resent as it was, whatever the repository picker shows now.
  handleCheckMoreResults = (message) => {
    if (!message || !message.continuationToken || !message.searchRequest) {
      this.appendLocalAiAssistantMessage(
        "Run the search again to check more results.",
      );
      return;
    }

    return this.searchWithAiNaturalLanguage(message.searchRequest.query, {
      continuation: {
        token: message.continuationToken,
        request: message.searchRequest,
        label: "Check more results",
      },
    });
  };

  handleReviseCriteria = (message) => {
    this.setState(
      {
        query:
          message && message.originalUserQuery ? message.originalUserQuery : "",
      },
      () => {
        this.focusChatInput(true);
        this.appendLocalAiAssistantMessage(
          "Update the search details, then send the revised request.",
        );
      },
    );
  };

  handleClarifySearch = (message) => {
    this.setState(
      {
        query:
          message && message.originalUserQuery ? message.originalUserQuery : "",
      },
      () => {
        this.focusChatInput(false);
        this.appendLocalAiAssistantMessage(
          "Add a name, address, permit number, block and lot, date, person description, or incident detail.",
        );
      },
    );
  };

  handleHelpfulFeedback = (message) => {
    this.submitAiFeedback(message, {
      rating: "Helpful",
      reasonCode: "Helpful",
      comment: "",
    });
  };

  handleShowNotHelpfulFeedback = (messageId) => {
    this.updateAiMessageFeedbackState(messageId, {
      showNotHelpfulFeedback: true,
      feedbackError: null,
      feedbackStatus: null,
    });
  };

  handleFeedbackReasonChange = (messageId, event) => {
    this.updateAiMessageFeedbackState(messageId, {
      feedbackReasonCode: event.target.value,
      feedbackError: null,
    });
  };

  handleFeedbackCommentChange = (messageId, event) => {
    this.updateAiMessageFeedbackState(messageId, {
      feedbackComment: event.target.value,
      feedbackError: null,
    });
  };

  handleSubmitNotHelpfulFeedback = (message) => {
    this.submitAiFeedback(message, {
      rating: "NotHelpful",
      reasonCode: message.feedbackReasonCode || "NotHelpful",
      comment: message.feedbackComment || "",
    });
  };

  submitAiFeedback = async (message, feedback) => {
    if (!message || !message.requestId || message.feedbackSubmitting) return;

    const requestBody = {
      requestId: message.requestId,
      rating: feedback.rating,
      reasonCode: feedback.reasonCode,
      comment: feedback.comment || "",
    };

    this.updateAiMessageFeedbackState(message.id, {
      feedbackSubmitting: true,
      feedbackError: null,
    });

    try {
      await submitAiSearchFeedback(requestBody);
      this.updateAiMessageFeedbackState(message.id, {
        feedbackSubmitting: false,
        feedbackStatus: "Feedback saved for review.",
        feedbackError: null,
        showNotHelpfulFeedback: false,
      });
    } catch (error) {
      console.error("AI FEEDBACK ERROR:", error);
      this.updateAiMessageFeedbackState(message.id, {
        feedbackSubmitting: false,
        feedbackError: "Feedback could not be saved. Please try again.",
      });
    }
  };

  getLocalAiDisplayAction = (query) => {
    const normalized = (query || "")
      .trim()
      .toLowerCase()
      .replace(/[?.!]+$/g, "");

    if (
      normalized === "show links" ||
      normalized === "show me the links" ||
      normalized === "give me links" ||
      normalized === "show viewer links"
    ) {
      return "links";
    }

    if (
      normalized === "show file numbers" ||
      normalized === "show file numbers only" ||
      normalized === "show file names" ||
      normalized === "show file names only" ||
      normalized === "list file names" ||
      normalized === "give me file names" ||
      normalized === "give me the file names"
    ) {
      return "filenames";
    }

    if (normalized === "show table" || normalized === "show as table") {
      return "table";
    }

    return null;
  };

  applyLocalAiDisplayAction = (displayMode, userQuery, targetMessage) => {
    if (!targetMessage) return;

    const acknowledgements = {
      links: "Here are the available viewer links.",
      filenames: "Here are the matching file names.",
      table: "Here are the matching documents in a table.",
    };

    this.setState(
      (previousState) => ({
        query: "",
        lastAiUserQuery: userQuery,
        aiChatMessages: (previousState.aiChatMessages || [])
          .concat(this.createAiUserMessage(userQuery))
          .map((message) =>
            message.id === targetMessage.id
              ? {
                  ...message,
                  showDocuments: true,
                  documentDisplayMode: displayMode,
                }
              : message,
          )
          .concat(
            this.createAiAssistantMessage(
              acknowledgements[displayMode],
              {
                requestId: null,
                totalMatches: 0,
                returnedDocumentCount: 0,
                summarizedDocumentCount: 0,
                broadCandidateCount: 0,
                rerankSubmittedCount: 0,
                rerankEvaluatedCount: 0,
                displayedDocumentCount: null,
                hasMoreMatches: false,
                totalMatchesIsExact: false,
                suggestedActions: [],
                searchPlan: null,
                aiAvailable: true,
                errorMessage: null,
              },
              [],
            ),
          ),
        aiConversationHistory: [
          ...(previousState.aiConversationHistory || []),
          { role: "user", content: userQuery },
          {
            role: "assistant",
            content: acknowledgements[displayMode],
          },
        ].slice(-6),
      }),
      this.scrollToBottom,
    );
  };

  handleAiNaturalLanguageSearchToggle = (event) => {
    if (!this.canUseAiNaturalLanguageSearch()) {
      this.setState({ isAiNaturalLanguageSearch: false });
      return;
    }

    const enabled = event.target.checked;

    this.setState({
      isAiNaturalLanguageSearch: enabled,
      aiError: null,
      showAiDocuments: false,
      aiDocumentDisplayMode: null,
    });
  };

  resetAiConversation = () => ({
    activeSearchSessionId: null,
    summarySelected: [],
    summaryResponse: null,
    summaryLoading: false,
    summaryError: null,
    ragNeedsNewSearch: false,
    ragEvidence: [],
    ragAnswerResponse: null,
    ragCoverageWarning: null,
    ragMissExplanation: null,
    ragStatus: null,
    ragQuestion: "",
    searchSessionDocumentCount: null,
    aiChatMessages: [],
    aiConversationHistory: [],
    aiDocuments: [],
    aiAnswer: "",
    aiSuggestedActions: [],
    aiSearchPlan: null,
    aiTotalMatches: 0,
    aiReturnedDocumentCount: 0,
    aiSummarizedDocumentCount: 0,
    aiBroadCandidateCount: 0,
    aiRerankSubmittedCount: 0,
    aiRerankEvaluatedCount: 0,
    aiDisplayedDocumentCount: null,
    aiHasMoreMatches: false,
    aiTotalMatchesIsExact: false,
    aiRequestId: null,
    aiSelectedDocumentCount: null,
    aiSearchMode: "",
    showAiDocuments: false,
    aiDocumentDisplayMode: null,
    lastAiUserQuery: "",
    aiError: null,
    aiLoading: false,
    aiAvailable: true,
  });

  handleChatSearchClose = () => {
    this.cancelAiRequest();
    this.setState(this.resetAiConversation(), () => {
      if (this.props.onClose) {
        this.props.onClose();
      }
    });
  };

  scrollToBottom = () => {
    const container = this.chatHistoryRef && this.chatHistoryRef.current;
    if (container) {
      // jump to bottom smoothly
      try {
        container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
      } catch (e) {
        container.scrollTop = container.scrollHeight;
      }
      return;
    }
    if (this.messagesEnd) {
      this.messagesEnd.scrollIntoView({ behavior: "smooth" });
    }
  };

  runExistingChatSearch = async (query) => {
    const parsed = this.parseQuery(query);
    this.setState({ lastParsedSearchText: parsed });

    this.addMessage({ type: "user", text: query });
    this.setState({ query: "", isLoading: true, error: "" });

    try {
      const scope = this.getScope();
      const payload = {
        repositoryId: scope.repositoryId,
        allRepositories: scope.allRepositories,
        query: query,
      };
      if (scope.templateId) payload.templateId = scope.templateId;
      const data = await chatSearchDocuments(payload);
      const normalizedResponse = this.normalizeChatSearchResponse(data);
      const results = normalizedResponse.results;

      console.log("FULL CHATSEARCH RESPONSE:", data);
      console.log("RESULT COUNT FROM CURRENT RESPONSE:", results.length);

      let parsedFromBackend = normalizedResponse.parsedTextSearch;
      if (!parsedFromBackend && results.length > 0) {
        parsedFromBackend =
          results[0].parsedTextSearch || results[0].searchText || null;
      }
      if (parsedFromBackend) {
        this.setState({ lastParsedSearchText: parsedFromBackend });
      }

      this.addMessage({
        type: "response",
        text: `Search results for "${query}"`,
        results: results,
      });
    } catch (error) {
      console.error("ChatSearch query failed", error);
      this.addMessage({
        type: "error",
        text: "Document Chat Search failed. Please try again.",
      });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  getFriendlyAiSearchError = (error) => {
    const denial = this.getEntitlementDenial(error);
    if (denial) {
      return (
        denial.message ||
        "DocumentSync AI is not available for your account."
      );
    }
    return "The AI search could not be completed. Please try again.";
  };

  searchWithAiNaturalLanguage = async (rawUserQuery, options = {}) => {
    if (!rawUserQuery || !rawUserQuery.trim()) {
      return;
    }

    // "Check more results" is the same search again, never a question about the shown results.
    const continuation = options.continuation || null;
    if (!continuation && this.state.ragNeedsNewSearch) {
      this.setState({
        aiError: "These results are no longer accessible. Choose New search.",
      });
      return;
    }
    if (!continuation && this.state.activeSearchSessionId)
      return this.searchWithinCurrentResults(rawUserQuery.trim());
    this.cancelAiRequest();
    const generation = this.aiRequestGeneration;
    const contextKey = this.aiContextKey();
    this.aiCancellation = createAiSearchCancellation();
    const isCurrent = () =>
      !this.aiUnmounted &&
      generation === this.aiRequestGeneration &&
      contextKey === this.aiContextKey();
    const query = rawUserQuery.trim();
    const displayAction = this.getLocalAiDisplayAction(query);
    const latestAssistantMessage =
      this.getLatestAssistantMessageWithDocuments();

    if (
      !continuation &&
      displayAction &&
      latestAssistantMessage &&
      Array.isArray(latestAssistantMessage.documents) &&
      latestAssistantMessage.documents.length > 0
    ) {
      this.applyLocalAiDisplayAction(
        displayAction,
        query,
        latestAssistantMessage,
      );
      return;
    }

    const previousDocumentIds = (this.state.aiDocuments || [])
      .map((document) => document.fileId)
      .filter((fileId) => fileId !== null && fileId !== undefined);

    // A continuation resends the request its token was issued for (query, repositories, template, client) with the
    // token, and no conversation context; the server refuses a token sent with any other request.
    const scope = this.getScope();
    const requestBody = continuation
      ? {
          repositoryId: continuation.request.repositoryId,
          allRepositories: continuation.request.allRepositories,
          query: continuation.request.query,
          clientId: continuation.request.clientId,
          continuationToken: continuation.token,
        }
      : {
          repositoryId: scope.allRepositories ? null : scope.repositoryId,
          allRepositories: Boolean(scope.allRepositories),
          query: query,
          conversationId: this.state.aiConversationId || null,
          history: this.getLimitedAiConversationHistory(),
          previousDocumentIds: previousDocumentIds,
          previousSearchPlan:
            options.previousSearchPlan !== undefined
              ? options.previousSearchPlan
              : this.state.aiSearchPlan || null,
          clientId:
            this.props.props && this.props.props.clientId !== undefined
              ? this.props.props.clientId
              : this.props.searchProps
                ? this.props.searchProps.clientId
                : null,
        };
    // A template restriction travels with the request (and with its continuation) only when one is set, so the
    // payload is unchanged for callers without a scope bar.
    const restrictedTemplateId = continuation
      ? continuation.request.templateId
      : scope.templateId;
    if (restrictedTemplateId !== null && restrictedTemplateId !== undefined) {
      requestBody.templateId = restrictedTemplateId;
    }
    const searchRequest = {
      repositoryId: requestBody.repositoryId,
      allRepositories: requestBody.allRepositories,
      query: requestBody.query,
      clientId: requestBody.clientId,
    };
    if (requestBody.templateId !== undefined) {
      searchRequest.templateId = requestBody.templateId;
    }

    this.setState(
      {
        query: "",
        aiLoading: true,
        aiError: null,
        lastAiUserQuery: query,
        aiChatMessages: (this.state.aiChatMessages || []).concat(
          this.createAiUserMessage(continuation ? continuation.label : query),
        ),
      },
      this.scrollToBottom,
    );
    this.notifySearching(true);

    try {
      const apiResponse = await aiNaturalLanguageSearch(
        requestBody,
        this.aiCancellation.token,
      );
      if (!isCurrent()) {
        this.notifySearching(false);
        return;
      }

      const normalized = this.normalizeBedrockAiSearchResponse(apiResponse);

      const documents = Array.isArray(normalized.documents)
        ? normalized.documents.map(this.normalizeAiDocument)
        : [];
      this.validateAiDocumentCounts(normalized, documents);

      const totalMatches =
        normalized.totalMatches !== null &&
        normalized.totalMatches !== undefined
          ? normalized.totalMatches
          : documents.length;

      const newHistory = [
        ...this.getLimitedAiConversationHistory(),
        { role: "user", content: query },
        {
          role: "assistant",
          content: normalized.answer || "I could not generate an answer.",
        },
      ].slice(-6);

      const assistantMessage = Object.assign(
        this.createAiAssistantMessage(
          normalized.answer || "I could not generate an answer.",
          normalized,
          documents,
          searchRequest.query,
        ),
        { searchRequest },
      );
      // Embedded in the Search screen, the assistant shows its documents at once, as a table, instead of waiting
      // for a "Show as table" click.
      if (
        this.isEmbedded() &&
        this.showsDocumentsInline() &&
        this.canOfferAiDocuments(assistantMessage)
      ) {
        assistantMessage.showDocuments = true;
        assistantMessage.documentDisplayMode = "table";
      }
      this.notifyDocuments(documents, normalized, {
        isLaterPage: Boolean(continuation),
      });

      this.setState(
        {
          activeSearchSessionId: normalized.searchSessionId,
          searchSessionDocumentCount: normalized.searchSessionDocumentCount,
          sessionIsLaterPage: Boolean(continuation),
          ragNeedsNewSearch: !normalized.searchSessionId,
          ragEvidence: [],
          ragAnswerResponse: null,
          ragCoverageWarning: null,
          ragMissExplanation: null,
          ragStatus: null,
          ragQuestion: "",
          aiAnswer: normalized.answer,
          aiDocuments: documents,
          aiSuggestedActions: normalized.suggestedActions,
          aiSearchPlan: normalized.searchPlan,
          aiTotalMatches: totalMatches,
          aiReturnedDocumentCount: normalized.returnedDocumentCount,
          aiSummarizedDocumentCount: normalized.summarizedDocumentCount,
          aiBroadCandidateCount: normalized.broadCandidateCount,
          aiRerankSubmittedCount: normalized.rerankSubmittedCount,
          aiRerankEvaluatedCount: normalized.rerankEvaluatedCount,
          aiDisplayedDocumentCount: normalized.displayedDocumentCount,
          aiHasMoreMatches: normalized.hasMoreMatches,
          aiTotalMatchesIsExact: normalized.totalMatchesIsExact,
          aiRequestId: normalized.requestId,
          aiSelectedDocumentCount: normalized.selectedDocumentCount,
          aiSearchMode: normalized.searchMode,
          aiConversationHistory: newHistory,
          aiConversationId:
            normalized.conversationId || this.state.aiConversationId || null,
          aiAvailable: normalized.aiAvailable,
          aiLoading: false,
          aiError:
            normalized.errorMessage ||
            (!normalized.searchSessionId
              ? NO_SESSION_ERROR
              : null),
          aiChatMessages: (this.state.aiChatMessages || []).concat(
            assistantMessage,
          ),
        },
        this.scrollToBottom,
      );
      this.notifySearching(false);
    } catch (error) {
      if (!isCurrent()) {
        this.notifySearching(false);
        return;
      }

      const errorMessage = this.getFriendlyAiSearchError(error);
      const denial = this.getEntitlementDenial(error);
      this.setState(
        {
          aiLoading: false,
          aiError: errorMessage,
          aiChatMessages: (this.state.aiChatMessages || []).concat(
            this.createAiErrorMessage(errorMessage, this.state.aiRequestId),
          ),
        },
        this.scrollToBottom,
      );
      this.notifySearching(false);
      if (denial) this.notifyEntitlementDenied(denial);
    }
  };

  searchWithinCurrentResults = async (question) => {
    const searchSessionId = this.state.activeSearchSessionId;
    if (!searchSessionId || this.state.ragNeedsNewSearch) return;
    this.cancelAiRequest();
    const generation = this.aiRequestGeneration;
    const contextKey = this.aiContextKey();
    this.aiCancellation = createAiSearchCancellation();
    // The previous follow-up of this session as shown: its question and the records and pages its answer cited. The
    // server may only narrow a follow-up that refers back to it ("Who signed them?"); it never widens the scope.
    const previousResponse = this.state.ragAnswerResponse;
    const previousTurn =
      previousResponse &&
      previousResponse.searchSessionId === searchSessionId &&
      this.state.ragQuestion
        ? {
            question: this.state.ragQuestion,
            documents: Object.values(
              (Array.isArray(previousResponse.claims)
                ? previousResponse.claims
                : []
              )
                .flatMap((claim) =>
                  claim && Array.isArray(claim.citations)
                    ? claim.citations
                    : [],
                )
                .filter(
                  (c) =>
                    c &&
                    Number.isInteger(c.templateId) &&
                    Number.isInteger(c.fileId),
                )
                .reduce((byRecord, c) => {
                  const key = c.templateId + ":" + c.fileId;
                  const pages = Array.isArray(c.pageNumbers)
                    ? c.pageNumbers.filter((p) => Number.isInteger(p) && p > 0)
                    : [];
                  byRecord[key] = byRecord[key] || {
                    templateId: c.templateId,
                    fileId: c.fileId,
                    pageNumbers: [],
                  };
                  byRecord[key].pageNumbers = [
                    ...new Set([...byRecord[key].pageNumbers, ...pages]),
                  ];
                  return byRecord;
                }, {}),
            ),
          }
        : null;
    const isCurrent = () =>
      !this.aiUnmounted &&
      generation === this.aiRequestGeneration &&
      contextKey === this.aiContextKey() &&
      searchSessionId === this.state.activeSearchSessionId;
    this.setState(
      {
        query: "",
        aiLoading: true,
        aiError: null,
        ragQuestion: question,
        ragEvidence: [],
        ragStatus: null,
        ragAnswerResponse: null,
        ragCoverageWarning: null,
        ragMissExplanation: null,
      },
      this.scrollToAskSection,
    );
    try {
      // One server-owned scoped retrieval + generation. Never substitute a global search.
      const response = await ragAnswer(
        previousTurn && previousTurn.documents.length > 0
          ? { searchSessionId, question, previousTurn }
          : { searchSessionId, question },
        this.aiCancellation.token,
      );
      if (!isCurrent()) return;
      if (!response || response.searchSessionId !== searchSessionId) {
        throw new Error("Invalid scoped answer response");
      }
      if (
        response.newSearchRequired ||
        ![
          "ANSWERED",
          "DEGRADED",
          "GENERATION_FAILED",
          "NO_RELEVANT_EVIDENCE",
        ].includes(response.status)
      ) {
        this.handleRagAnswerFailure(response);
        return;
      }
      const evidencePackage = response.evidencePackage;
      if (
        evidencePackage &&
        evidencePackage.searchSessionId !== searchSessionId
      ) {
        throw new Error("Invalid evidence package session");
      }
      const answerStatus = ["ANSWERED", "DEGRADED"].includes(response.status);
      const hasAnswer =
        typeof response.answer === "string" && Boolean(response.answer.trim());
      const claims = Array.isArray(response.claims) ? response.claims : [];
      const isOptionalLabel = (value) =>
        value === null || value === undefined || typeof value === "string";
      // Validate presentation shape only. Qualification and citation identity remain server-owned.
      if (
        answerStatus &&
        (response.status === "ANSWERED" || hasAnswer || claims.length > 0) &&
        (!hasAnswer ||
          claims.length === 0 ||
          !claims.every(
            (claim) =>
              claim &&
              typeof claim.text === "string" &&
              claim.text.trim() &&
              isOptionalLabel(claim.documentLabel) &&
              Array.isArray(claim.citations) &&
              claim.citations.length > 0 &&
              claim.citations.every(
                (citation) =>
                  citation &&
                  isOptionalLabel(citation.documentLabel) &&
                  Number.isInteger(citation.templateId) &&
                  citation.templateId > 0 &&
                  Number.isInteger(citation.fileId) &&
                  citation.fileId > 0 &&
                  typeof citation.quote === "string" &&
                  citation.quote.trim(),
              ),
          ))
      ) {
        throw new Error("Invalid answer presentation contract");
      }
      const evidence =
        response.status !== "NO_RELEVANT_EVIDENCE" &&
        evidencePackage &&
        Array.isArray(evidencePackage.evidence)
          ? evidencePackage.evidence.filter(
              (item) => item && typeof item === "object",
            )
          : [];
      const sessionCount = evidencePackage
        ? this.getSearchSessionDocumentCount(evidencePackage)
        : null;
      const warning = (
        response.status === "GENERATION_FAILED"
          ? [evidencePackage && evidencePackage.coverageWarning]
          : [
              response.coverageWarning,
              evidencePackage && evidencePackage.coverageWarning,
            ]
      ).find((value) => typeof value === "string" && value.trim());
      this.setState(
        {
          aiLoading: false,
          ragEvidence: evidence,
          ragStatus: response.status,
          ragAnswerResponse: answerStatus && hasAnswer ? response : null,
          // No answer: where the server looked and a new search of the user's own words (server-built, masked).
          ragMissExplanation:
            !(answerStatus && hasAnswer) && response.missExplanation && typeof response.missExplanation === "object"
              ? response.missExplanation
              : null,
          ragCoverageWarning:
            warning ||
            (response.status === "DEGRADED"
              ? "Coverage is incomplete. Any answer is grounded in qualified retrieved passages and may not cover every relevant passage."
              : null),
          searchSessionDocumentCount:
            sessionCount !== null
              ? sessionCount
              : this.state.searchSessionDocumentCount,
          aiError:
            response.status === "GENERATION_FAILED"
              ? "I couldn't put an answer together this time. The passages I found are below, and you can ask again."
              : null,
        },
        this.scrollToAskSection,
      );
    } catch (error) {
      if (!isCurrent()) return;
      this.handleRagAnswerFailure(
        error.response && error.response.data,
        error.response && error.response.status,
        !!error.__CANCEL__,
      );
    }
  };

  handleRagAnswerFailure = (body, httpStatus, canceled = false) => {
    const status = body && body.status;
    const denied = status === "SENSITIVE_DENIED";
    // 403 with AI_NOT_ENTITLED: the user may still see these results, just not ask the AI about them.
    if (
      httpStatus === 403 &&
      body &&
      body.errorCode === AI_NOT_ENTITLED_ERROR_CODE
    ) {
      this.setState({
        aiLoading: false,
        ragEvidence: [],
        ragAnswerResponse: null,
        ragCoverageWarning: null,
        ragStatus: null,
        aiError:
          body.message || "DocumentSync AI is not available for your account.",
      });
      this.notifyEntitlementDenied(body);
      return;
    }
    const inaccessible =
      [401, 404, 410].includes(httpStatus) ||
      (httpStatus === 403 && !denied) ||
      Boolean(body && body.newSearchRequired) ||
      [
        "SESSION_EXPIRED",
        "SESSION_NOT_ACCESSIBLE",
        "EMPTY_AUTHORIZED_SCOPE",
      ].includes(status);
    const knownStatus = [
      "SENSITIVE_DENIED",
      "SESSION_EXPIRED",
      "SESSION_NOT_ACCESSIBLE",
      "EMPTY_AUTHORIZED_SCOPE",
      "RETRIEVAL_FAILED",
      "RETRIEVAL_TIMEOUT",
      "INVALID_REQUEST",
    ].includes(status)
      ? status
      : null;
    this.setState({
      aiLoading: false,
      ragEvidence: [],
      ragAnswerResponse: null,
      ragCoverageWarning: null,
      ragMissExplanation:
        body && body.missExplanation && typeof body.missExplanation === "object" ? body.missExplanation : null,
      ragStatus: knownStatus,
      ragNeedsNewSearch: inaccessible,
      activeSearchSessionId: inaccessible
        ? null
        : this.state.activeSearchSessionId,
      aiError: inaccessible
        ? "These search results have expired or are no longer accessible. Choose New search."
        : denied
          ? "I can't share that: it asks for private information such as a social security number. You can ask about other details in these records."
          : canceled
            ? "The question was canceled. You can ask again or choose New search."
            : "We couldn't answer this question. Try again or choose New search.",
    });
  };

  scrollToAiResultSection = (messageId) => {
    const container = this.chatHistoryRef && this.chatHistoryRef.current;
    const resultSection = this.aiResultSectionRefs[messageId];
    if (!container || !resultSection) return;

    const containerRect = container.getBoundingClientRect();
    const sectionRect = resultSection.getBoundingClientRect();
    const headerOffset = 12;
    const nextScrollTop =
      container.scrollTop + sectionRect.top - containerRect.top - headerOffset;

    container.scrollTop = Math.max(0, nextScrollTop);
  };

  getBlockLotSearchError = (error) => blockLotSearchError(error);

  handleBlockLotSubmit = async (event) => {
    event.preventDefault();
    if (this.state.blockLotLoading) return;

    const block = (this.state.block || "").trim();
    const lot = (this.state.lot || "").trim();

    if (!block && !lot) {
      this.setState({
        blockLotError: "Enter a Block, a Lot, or both to search.",
        blockLotSubmitted: false,
      });
      return;
    }

    if (block.length > 100 || lot.length > 100) {
      this.setState({
        blockLotError: "Block and Lot must each be 100 characters or fewer.",
        blockLotSubmitted: false,
      });
      return;
    }

    const request = {
      block: block || null,
      lot: lot || null,
      repositoryId: this.state.allRepositories
        ? null
        : this.state.selectedRepositoryId || null,
      allRepositories: Boolean(this.state.allRepositories),
    };

    this.setState({
      block,
      lot,
      blockLotLoading: true,
      blockLotError: "",
      blockLotSubmitted: false,
    });

    try {
      const data = await searchByBlockAndLot(request);
      const normalized = this.normalizeBlockLotSearchResponse(data);
      this.setState({
        blockLotResults: normalized.results,
        blockLotResultCount: normalized.resultCount,
        blockLotIsTruncated: normalized.isTruncated,
        blockLotTemplatesEvaluated: normalized.templatesEvaluated,
        blockLotTemplatesSearched: normalized.templatesSearched,
        blockLotSubmitted: true,
        blockLotSearchedBlock: block,
        blockLotSearchedLot: lot,
      });
    } catch (error) {
      this.setState({
        blockLotError: this.getBlockLotSearchError(error),
        blockLotSubmitted: false,
      });
    } finally {
      this.setState({ blockLotLoading: false });
    }
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    if (this.state.isLoading || this.state.aiLoading) return;
    const rawQuery = this.state.query;
    const query = rawQuery.trim();
    if (!query) return;

    if (
      this.state.isAiNaturalLanguageSearch &&
      this.canUseAiNaturalLanguageSearch()
    ) {
      return this.searchWithAiNaturalLanguage(rawQuery);
    }

    return this.runExistingChatSearch(query);
  };

  async getRefreshToken() {
    const user = await Auth.currentAuthenticatedUser();
    const refreshToken = user.signInUserSession?.refreshToken?.token;
    return refreshToken;
  }

  openViewer = async (file, options = {}) => {
    if (!file) return;
    // The Search screen opens documents in its own viewer overlay so every mode behaves the same.
    if (typeof this.props.onViewDocument === "function") {
      this.props.onViewDocument(file, options);
      return;
    }
    if (this.isViewerOpening) return;
    this.isViewerOpening = true;
    setTimeout(() => (this.isViewerOpening = false), 1500);

    const fileId = file.fileId || file.FileId;
    const fileName = file.fileName || file.FileName || "Document";
    const clientId = this.props.searchProps?.clientId;
    const userEmailId = this.props.searchProps?.loggedInUser;
    const refreshToken = await this.getRefreshToken();
    const targetOrigin = config.ImageViewer;
    const suppressViewerSearch = options.suppressViewerSearch === true;
    // The cited page to open, where the clicked citation proves one; the viewer goes there only when the document has it.
    const pageNumber =
      Number.isInteger(options.pageNumber) && options.pageNumber > 0
        ? options.pageNumber
        : undefined;
    // The OCR page count the server read for the record: the viewer opens the page only where the image has as many pages.
    const pageCount =
      pageNumber && Number.isInteger(options.pageCount) && options.pageCount > 0
        ? options.pageCount
        : undefined;
    const viewerPayload = file.viewerPayload || {};
    const viewerTextSearch =
      !suppressViewerSearch && this.props.searchProps?.hasTextSearch
        ? viewerPayload.textSearch ||
          viewerPayload.searchText ||
          viewerPayload.parsedTextSearch ||
          this.state.lastParsedSearchText
        : undefined;

    const message = {
      refreshToken: refreshToken,
      fileId: fileId,
      editIndex: this.props.searchProps?.hasEditIndex,
      manageRedaction: this.props.searchProps?.hasManageRedaction,
      deletePages: this.props.searchProps?.hasDelete,
      fileName: fileName,
      userEmailId: userEmailId,
      managePii: this.props.searchProps?.hasPIIRole,
      clientId: clientId,
      printWithAnnotations: this.props.searchProps?.hasPrintWithAnnotationsRole,
      cantoggleRedactionColor: this.props.searchProps?.hasWhiteRedaction,
      redactionColor: this.props.searchProps?.redactionColor || "Black",
      ...viewerPayload,
    };
    if (pageNumber) message.pageNumber = pageNumber;
    if (pageCount) message.pageCount = pageCount;
    if (!suppressViewerSearch && viewerTextSearch) {
      message.textSearch = viewerTextSearch;
    } else if (suppressViewerSearch) {
      delete message.textSearch;
      delete message.searchText;
      delete message.parsedTextSearch;
    }

    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      const viewerUrl = `${config.ImageViewer}?fileId=${encodeURIComponent(
        fileId,
      )}&fileName=${encodeURIComponent(fileName)}&clientId=${encodeURIComponent(
        clientId || "",
      )}&userEmailId=${encodeURIComponent(userEmailId || "")}&refreshToken=${encodeURIComponent(
        refreshToken || "",
      )}${pageNumber ? `&pageNumber=${pageNumber}` : ""}${pageCount ? `&pageCount=${pageCount}` : ""}`;

      if (
        !window.__chatSearchViewerTab ||
        window.__chatSearchViewerTab.closed
      ) {
        window.__chatSearchViewerTab = window.open(
          viewerUrl,
          "chatSearchViewerTab",
          "noopener,noreferrer",
        );
      } else {
        window.__chatSearchViewerTab.focus();
      }
      return;
    }

    this.setState({ viewerOpen: true, viewerFile: file });

    setTimeout(() => {
      const imageViewer = document.getElementById("chatSearchImageViewer");
      if (!imageViewer) {
        console.error("chatSearchImageViewer iframe not found");
        return;
      }

      const postMessage = () => {
        imageViewer.contentWindow.postMessage(
          JSON.stringify(message),
          targetOrigin,
        );
      };

      if (imageViewer.attachEvent) {
        imageViewer.attachEvent("onload", postMessage);
      } else {
        imageViewer.onload = postMessage;
      }

      imageViewer.src = config.ImageViewer;
    }, 300);
  };

  closeViewer = () => {
    this.setState({ viewerOpen: false });
  };

  // The OCR page count the server read for a record (viewerPageCount), from any citation of that record in the answer
  // shown; the viewer opens a cited page only when the image has as many pages.
  citedPageCount = (item) => {
    const response = this.state.ragAnswerResponse;
    const cited = (
      response && Array.isArray(response.claims) ? response.claims : []
    )
      .flatMap((claim) =>
        claim && Array.isArray(claim.citations) ? claim.citations : [],
      )
      .find(
        (c) =>
          c &&
          item &&
          c.templateId === item.templateId &&
          c.fileId === item.fileId &&
          Number.isInteger(c.viewerPageCount) &&
          c.viewerPageCount > 0,
      );
    return cited ? cited.viewerPageCount : undefined;
  };

  openAiDocument = (document, pageNumber, pageCount) => {
    const existingResultShape = {
      fileId: document.fileId,
      FileId: document.fileId,
      fileName: document.fileName,
      FileName: document.fileName,
      repositoryName: document.repositoryName,
      RepositoryName: document.repositoryName,
      templateName: document.templateName,
      TemplateName: document.templateName,
      templateId: document.templateId,
      TemplateId: document.templateId,
      repositoryId: document.repositoryId,
      RepositoryId: document.repositoryId,
      viewerUrl: document.viewerUrl,
    };

    this.openViewer(
      existingResultShape,
      Number.isInteger(pageCount)
        ? { suppressViewerSearch: true, pageNumber, pageCount }
        : { suppressViewerSearch: true, pageNumber },
    );
  };

  getAiMessageById = (messageId) => {
    const messages = Array.isArray(this.state.aiChatMessages)
      ? this.state.aiChatMessages
      : [];
    return messages.find((message) => message.id === messageId) || null;
  };

  handleAiSuggestedAction = (action, messageId) => {
    const actionInfo =
      action && typeof action === "object"
        ? action
        : {
            label: String(action || ""),
            actionType: "SUBMIT_FOLLOW_UP",
            value: action,
            followUpQuery: "",
          };
    const actionType = String(
      actionInfo.actionType || actionInfo.type || "",
    ).toUpperCase();
    const normalized = (
      actionInfo.label ||
      actionInfo.value ||
      ""
    ).toLowerCase();
    const message = this.getAiMessageById(messageId);

    if (actionType === "SHOW_LINKS" || normalized.indexOf("viewer link") >= 0) {
      if (this.canOfferAiDocuments(message)) {
        this.updateAiMessageDocumentDisplay(messageId, "links");
      }
      return;
    }

    if (
      actionType === "SHOW_FILE_NAMES" ||
      normalized.indexOf("file name") >= 0 ||
      normalized.indexOf("file number") >= 0
    ) {
      if (this.canOfferAiDocuments(message)) {
        this.updateAiMessageDocumentDisplay(messageId, "filenames");
      }
      return;
    }

    if (actionType === "SHOW_TABLE" || normalized.indexOf("table") >= 0) {
      if (this.canOfferAiDocuments(message)) {
        this.updateAiMessageDocumentDisplay(messageId, "table");
      }
      return;
    }

    if (actionType === "SHOW_BROADER_MATCHES") {
      this.handleReviewBroaderMatches(message, actionInfo);
      return;
    }

    if (actionType === "REVISE_CRITERIA") {
      this.handleReviseCriteria(message);
      return;
    }

    if (actionType === "CLARIFY_SEARCH") {
      this.handleClarifySearch(message);
      return;
    }

    if (actionType === "CHECK_MORE_RESULTS") {
      return this.handleCheckMoreResults(message);
    }

    if (actionType === "SUBMIT_FOLLOW_UP" && actionInfo.followUpQuery) {
      this.searchWithAiNaturalLanguage(actionInfo.followUpQuery, {
        previousSearchPlan: message
          ? message.searchPlan || this.state.aiSearchPlan || null
          : this.state.aiSearchPlan || null,
      });
      return;
    }

    this.setState(
      {
        query:
          actionInfo.followUpQuery ||
          actionInfo.label ||
          actionInfo.value ||
          "",
      },
      () => this.focusChatInput(true),
    );
  };

  renderSafeHighlight = (highlight) => {
    return String(highlight || "");
  };

  renderAiLoading = () => (
    <div className="chat-search-ai-message-assistant" role="status">
      <div className="chat-search-ai-response chat-search-ai-loading">
        <div className="chat-search-ai-header">
          <span className="chat-search-ai-badge">AI</span>
          <span>DocumentSync Assistant</span>
        </div>
        <div className="chat-search-ai-answer">
          AI is searching DocumentSync...
        </div>
      </div>
    </div>
  );

  // In the redesigned tabs a search that found nothing has no session to ask about, and its own answer already says
  // so: the no-questions line is left out there.
  visibleAiError = () =>
    this.props.designLayout && this.state.aiError === NO_SESSION_ERROR && !(this.state.aiDocuments || []).length
      ? null
      : this.state.aiError;

  renderAiError = () => {
    const error = this.visibleAiError();
    if (!error) return null;
    return <div className="chat-search-ai-error">{error}</div>;
  };

  renderAiSuggestedActions = (message) => {
    let actions = this.normalizeSuggestedActions(message.suggestedActions);
    const inlineDocuments = this.showsDocumentsInline();
    if (!inlineDocuments) {
      // The Search screen's grid shows the documents; the display-mode actions have nothing to switch. The server
      // may send them as follow-up actions carrying only their label, so they are recognised by label too.
      const displayLabels = ["show as table", "show file names only", "show file names", "show file numbers", "show file numbers only", "show viewer links"];
      actions = actions.filter(
        (action) =>
          !["SHOW_TABLE", "SHOW_FILE_NAMES", "SHOW_LINKS"].includes(
            String(action.actionType || "").toUpperCase(),
          ) && !displayLabels.includes(String(action.label || action.value || "").trim().toLowerCase()),
      );
    }
    if (inlineDocuments && this.canOfferAiDocuments(message)) {
      actions = actions.concat([
        {
          label: "Show as table",
          actionType: "SHOW_TABLE",
          value: "Show as table",
        },
        {
          label: "Show file names only",
          actionType: "SHOW_FILE_NAMES",
          value: "Show file names only",
        },
        {
          label: "Show viewer links",
          actionType: "SHOW_LINKS",
          value: "Show viewer links",
        },
      ]);
      const seen = {};
      actions = actions.filter((action) => {
        const key = String(action.label || "").toLowerCase();
        if (!key || seen[key]) return false;
        seen[key] = true;
        return true;
      });
    }
    if (actions.length === 0 || !this.canShowAiSuggestedActions(message)) {
      return null;
    }

    return (
      <div className="chat-search-ai-actions">
        {actions.map((action) => (
          <button
            type="button"
            key={action.label}
            className="chat-search-ai-action"
            onClick={() => this.handleAiSuggestedAction(action, message.id)}
          >
            {action.label}
          </button>
        ))}
      </div>
    );
  };

  renderAiFeedbackControls = (message) => {
    if (!message.requestId || message.role !== "assistant") {
      return null;
    }

    const reasonOptions = [
      { value: "WrongDocument", label: "Wrong document" },
      { value: "MissingDocument", label: "Missing document" },
      { value: "TooBroad", label: "Too broad" },
      { value: "TooNarrow", label: "Too narrow" },
      { value: "IncorrectExplanation", label: "Incorrect explanation" },
      { value: "IncorrectFactConnection", label: "Incorrect fact connection" },
      { value: "UnsafeResponse", label: "Unsafe response" },
      { value: "NotHelpful", label: "Other / Not helpful" },
    ];
    const feedbackSaved = Boolean(message.feedbackStatus);

    return (
      <div className="chat-search-ai-feedback">
        {!feedbackSaved && (
          <div className="chat-search-ai-feedback-actions">
            <button
              type="button"
              className="chat-search-ai-feedback-button"
              disabled={message.feedbackSubmitting}
              onClick={() => this.handleHelpfulFeedback(message)}
            >
              Helpful
            </button>
            <button
              type="button"
              className="chat-search-ai-feedback-button"
              disabled={message.feedbackSubmitting}
              onClick={() => this.handleShowNotHelpfulFeedback(message.id)}
            >
              Not helpful
            </button>
          </div>
        )}

        {message.showNotHelpfulFeedback && !feedbackSaved && (
          <div className="chat-search-ai-feedback-form">
            <select
              value={message.feedbackReasonCode || "NotHelpful"}
              onChange={(event) =>
                this.handleFeedbackReasonChange(message.id, event)
              }
            >
              {reasonOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              maxLength="300"
              value={message.feedbackComment || ""}
              onChange={(event) =>
                this.handleFeedbackCommentChange(message.id, event)
              }
              placeholder="Optional comment"
            />
            <button
              type="button"
              className="chat-search-ai-feedback-button"
              disabled={message.feedbackSubmitting}
              onClick={() => this.handleSubmitNotHelpfulFeedback(message)}
            >
              Send feedback
            </button>
          </div>
        )}

        {message.feedbackStatus && (
          <div className="chat-search-ai-feedback-status">
            {message.feedbackStatus}
          </div>
        )}
        {message.feedbackError && (
          <div className="chat-search-ai-feedback-error">
            {message.feedbackError}
          </div>
        )}
      </div>
    );
  };

  renderSummaryCheckbox = (document) => {
    const key = summaryDocumentKey(document);
    const selected = this.state.summarySelected.includes(key);
    const current = this.state.aiDocuments.some(
      (d) => summaryDocumentKey(d) === key,
    );
    if (
      !current ||
      !this.state.activeSearchSessionId ||
      this.state.ragNeedsNewSearch
    )
      return null;
    return (
      <input
        type="checkbox"
        className="summary-result-checkbox"
        checked={selected}
        aria-label={`Select ${document.fileName || "document"} for summary`}
        disabled={
          this.state.aiLoading ||
          this.state.summaryLoading ||
          (!selected && this.state.summarySelected.length >= 4)
        }
        onChange={() => this.toggleSummarySelection(key)}
      />
    );
  };

  renderAiDocumentCards = (documents) => (
    <div className="chat-search-ai-document-list">
      {documents.map((document, index) => (
        <div
          className="chat-search-ai-document-card"
          key={`${document.fileId}-${document.templateId || "template"}-${document.candidateOrdinal || "candidate"}-${index}`}
        >
          <div className="chat-search-ai-document-name">
            {this.renderSummaryCheckbox(document)}
            {document.fileName || `File ${document.fileId}`}
          </div>
          <div className="chat-search-ai-document-meta">
            {document.repositoryName}
            {document.repositoryName && document.templateName ? "  " : ""}
            {this.formatTemplateName(document.templateName)}
          </div>
          <div className="chat-search-ai-document-reason">
            {document.reason ||
              "Ranked as relevant based on the available OCR text."}
          </div>
          {document.highlights && document.highlights.length > 0 && (
            <div className="chat-search-ai-document-highlight">
              {this.renderSafeHighlight(document.highlights[0])}
            </div>
          )}
          <button
            type="button"
            className="chat-search-ai-view-button"
            onClick={() => this.openAiDocument(document)}
          >
            View
          </button>
        </div>
      ))}
    </div>
  );

  renderAiFileNames = (documents) => (
    <ul className="chat-search-ai-file-name-list">
      {documents.map((document, index) => (
        <li
          key={`${document.fileId}-${document.templateId || "template"}-${document.candidateOrdinal || "candidate"}-${index}`}
        >
          {this.renderSummaryCheckbox(document)}
          <button type="button" onClick={() => this.openAiDocument(document)}>
            {document.fileName || `File ${document.fileId}`}
          </button>
        </li>
      ))}
    </ul>
  );

  renderAiViewerLinks = (documents) => (
    <ul className="chat-search-ai-file-name-list">
      {documents.map((document, index) => (
        <li
          key={`${document.fileId}-${document.templateId || "template"}-${document.candidateOrdinal || "candidate"}-${index}`}
        >
          {this.renderSummaryCheckbox(document)}
          <button type="button" onClick={() => this.openAiDocument(document)}>
            View {document.fileName || `File ${document.fileId}`}
          </button>
        </li>
      ))}
    </ul>
  );

  // Shown as on the Index Search grid: only to users who manage redactions. "Unknown" when the server could not read
  // the record's flag.
  showsRedactionColumn = () =>
    Boolean((this.props.searchProps || this.props.props || {}).hasManageRedaction);

  redactionLabel = (document) =>
    document.hasAnnotations === true
      ? "Yes"
      : document.hasAnnotations === false
        ? "No"
        : "Unknown";

  renderAiTable = (documents) => (
    <div className="chat-search-ai-table-wrapper">
      <table className="chat-search-ai-table">
        <thead>
          <tr>
            <th>File</th>
            <th>Repository</th>
            <th>Template</th>
            <th>Match reason</th>
            {this.showsRedactionColumn() && <th>Redaction</th>}
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {documents.map((document, index) => (
            <tr
              key={`${document.fileId}-${document.templateId || "template"}-${document.candidateOrdinal || "candidate"}-${index}`}
            >
              <td>
                {this.renderSummaryCheckbox(document)}
                {document.fileName || `File ${document.fileId}`}
              </td>
              <td>{document.repositoryName}</td>
              <td>{this.formatTemplateName(document.templateName)}</td>
              <td>
                {document.reason ||
                  "Ranked as relevant based on the available OCR text."}
              </td>
              {this.showsRedactionColumn() && (
                <td>{this.redactionLabel(document)}</td>
              )}
              <td>
                <button
                  type="button"
                  onClick={() => this.openAiDocument(document)}
                >
                  View
                </button>
                {/* TODO: Add per-document download after an existing authorized download handler is confirmed. */}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  renderAiDocumentsByMode = (message) => {
    const documents = Array.isArray(message.documents) ? message.documents : [];
    const displayMode = message.documentDisplayMode;
    if (documents.length === 0) {
      return null;
    }

    if (displayMode === "filenames") {
      return this.renderAiFileNames(documents);
    }

    if (displayMode === "table") {
      return this.renderAiTable(documents);
    }

    if (displayMode === "links") {
      return this.renderAiViewerLinks(documents);
    }

    return null;
  };

  renderAiSearchConversation = () => {
    const messages = Array.isArray(this.state.aiChatMessages)
      ? this.state.aiChatMessages
      : [];

    return (
      <div className="chat-search-ai-conversation">
        {messages.map((message) => {
          if (message.role === "user") {
            return (
              <div className="chat-search-ai-message-user" key={message.id}>
                <div className="chat-search-ai-user-bubble">
                  {message.content}
                </div>
              </div>
            );
          }

          const statusText = this.getAiStatusText(message);
          const hasDocuments =
            Array.isArray(message.documents) && message.documents.length > 0;
          const canOfferDocuments = this.canOfferAiDocuments(message);
          const canShowDocuments = this.canShowAiDocuments(message);
          const responseType = String(message.responseType || "").toUpperCase();

          return (
            <div className="chat-search-ai-message-assistant" key={message.id}>
              <div className="chat-search-ai-response">
                <div className="chat-search-ai-header">
                  <span className="chat-search-ai-badge">AI</span>
                  <span>DocumentSync Assistant</span>
                </div>
                <div className="chat-search-ai-answer">
                  {this.props.designLayout ? readableServerText(message.content) : message.content}
                </div>
                {statusText && (
                  <div className="chat-search-ai-counts">
                    <div>{statusText}</div>
                  </div>
                )}
                {this.props.designLayout && message.searchHelp && (
                  <SearchHelpCard
                    help={message.searchHelp}
                    onSearch={this.startSuggestedSearch}
                    disabled={this.state.aiLoading}
                  />
                )}
                {responseType === "NO_EXACT_MATCH_WITH_BROADER_RESULTS" &&
                  message.hasBroaderMatches &&
                  message.broaderMatchCount > 0 && (
                    <div className="chat-search-ai-counts">
                      <div>
                        {message.broaderMatchCount} broader possible{" "}
                        {message.broaderMatchCount === 1 ? "match" : "matches"}{" "}
                        may be available.
                      </div>
                    </div>
                  )}
                {message.aiAvailable === false && hasDocuments && (
                  <div className="chat-search-ai-unavailable">
                    The AI summary is unavailable, but matching documents are
                    still available.
                  </div>
                )}
                {message.errorMessage && (
                  <div className="chat-search-ai-error">
                    {message.errorMessage}
                    {message.requestId && (
                      <div>Reference: {message.requestId}</div>
                    )}
                  </div>
                )}
                {this.renderAiSuggestedActions(message)}
                {this.renderAiFeedbackControls(message)}
              </div>

              {canOfferDocuments &&
                !canShowDocuments &&
                this.showsDocumentsInline() && (
                  <div className="chat-search-ai-documents-available">
                    Matching documents are available. Choose a view above to
                    show them.
                  </div>
                )}

              {canShowDocuments && this.showsDocumentsInline() && (
                <div
                  ref={(el) => {
                    if (el) {
                      this.aiResultSectionRefs[message.id] = el;
                    } else {
                      delete this.aiResultSectionRefs[message.id];
                    }
                  }}
                  className="chat-search-ai-result-section"
                >
                  {this.renderAiDocumentsByMode(message)}
                </div>
              )}
            </div>
          );
        })}

        {this.state.aiLoading &&
          !this.state.activeSearchSessionId &&
          this.renderAiLoading()}
      </div>
    );
  };

  scrollToAskSection = () => {
    if (this.askResultsSection) {
      this.askResultsSection.scrollIntoView({
        block: "start",
        behavior: "smooth",
      });
    }
  };

  getEvidenceDocument = (item) => {
    // Match both physical identifiers; a file ID alone is not a metadata join.
    const hasIdentity = item.templateId != null && item.fileId != null;
    const original =
      hasIdentity &&
      this.state.aiDocuments.find(
        (document) =>
          String(document.templateId) === String(item.templateId) &&
          String(document.fileId) === String(item.fileId),
      );
    const text = (...values) =>
      values.find((value) => typeof value === "string" && value.trim()) || "";
    const title =
      text(
        item.fileName,
        item.documentName,
        item.displayName,
        original && original.fileName,
        item.documentTitle,
        item.templateName,
        original && original.templateName,
        item.viewerLabel,
      ) || "Document";
    // Use the existing authenticated viewer, never an arbitrary evidence URL.
    const validId = (value) => /^[1-9]\d*$/.test(String(value));
    const document =
      hasIdentity && validId(item.templateId) && validId(item.fileId)
        ? {
            ...(original || {}),
            templateId: item.templateId,
            fileId: item.fileId,
            fileName: title,
          }
        : null;
    return { title, document };
  };

  renderSearchComposer = (isAiSearch, followUp = false) => {
    const isSearching =
      this.state.isLoading || (isAiSearch && this.state.aiLoading);
    const unavailable = followUp && this.state.ragNeedsNewSearch;
    const embeddedAssistant =
      this.isEmbedded() && this.getEmbeddedMode() === CHAT_SEARCH_MODE_ASSISTANT;
    const placeholder = isAiSearch
      ? followUp
        ? "Ask a question about these results..."
        : embeddedAssistant
          ? "Ask a question about your documents..."
          : "Search documents with AI..."
      : REGULAR_SEARCH_PLACEHOLDER;
    return (
      <form
        className={
          "chat-search-composer" + (followUp ? " chat-search-ask-composer" : "")
        }
        onSubmit={this.handleSubmit}
      >
        <input
          id="chatSearchQuery"
          ref={this.chatInputRef}
          type="text"
          value={this.state.query}
          onChange={this.handleQueryChange}
          placeholder={placeholder}
          aria-label={isAiSearch ? placeholder : "Chat query"}
          aria-describedby={followUp ? "chatSearchAskScope" : undefined}
          disabled={isSearching || unavailable}
        />
        <button
          type="submit"
          className="chat-search-send-button"
          disabled={!this.state.query.trim() || isSearching || unavailable}
        >
          {isSearching
            ? followUp
              ? "Answering..."
              : "Searching..."
            : isAiSearch
              ? followUp || embeddedAssistant
                ? "Ask"
                : "Search"
              : "Send"}
        </button>
      </form>
    );
  };

  // While a result set is open, the follow-up question box and New search stay in one bar below the scrolling
  // results, where the search box was: both are reachable without scrolling, whatever is shown above.
  renderSessionBar = () => (
    <div className="chat-search-session-bar">
      {this.renderSearchComposer(true, true)}
      <button
        type="button"
        className="chat-search-ai-action chat-search-new-search"
        onClick={this.beginNewAiSearch}
        title="Clear these results and start a new search"
      >
        New search
      </button>
    </div>
  );

  renderAskResults = () => {
    const {
      activeSearchSessionId,
      ragNeedsNewSearch,
      searchSessionDocumentCount,
      ragQuestion,
      ragEvidence,
      ragStatus,
      ragAnswerResponse,
      ragCoverageWarning,
      ragMissExplanation,
      aiLoading,
      aiError,
    } = this.state;
    if (!activeSearchSessionId && !ragNeedsNewSearch) return null;
    const countKnown = searchSessionDocumentCount !== null;
    return (
      <section
        className="chat-search-ask-section"
        aria-labelledby="chatSearchAskTitle"
        ref={(element) => {
          this.askResultsSection = element;
        }}
      >
        {!this.props.designLayout && (<React.Fragment>
        <div className="chat-search-ask-header">
          <div>
            <h3 id="chatSearchAskTitle">Ask AI about these results</h3>
          </div>
        </div>
        <p id="chatSearchAskScope" className="chat-search-ask-scope">
          {ragNeedsNewSearch
            ? "Choose New search to ask questions about a new set of results."
            : countKnown
              ? `Ask questions using only the current ${searchSessionDocumentCount} search ${searchSessionDocumentCount === 1 ? "result" : "results"}.` +
                (this.state.sessionIsLaterPage
                  ? ' These are the results of "Check more results" only: follow-up questions, summaries and reports cover this page\'s records, not earlier pages.'
                  : "")
              : `Ask questions within your current search. ${this.state.aiDocuments.length} ${this.state.aiDocuments.length === 1 ? "document" : "documents"} displayed.`}
        </p>
        </React.Fragment>)}
        {!ragNeedsNewSearch && !this.props.designLayout && (
          <div
            className="chat-search-quick-questions"
            aria-label="Suggested questions"
          >
            {/* Shown for every search, whatever the records are about: only questions that fit any result set. */}
            {["What other details are mentioned?"].map((question) => (
              <button
                key={question}
                type="button"
                className="chat-search-ai-action"
                disabled={aiLoading}
                onClick={() => this.searchWithAiNaturalLanguage(question)}
              >
                {question}
              </button>
            ))}
          </div>
        )}
        {ragQuestion && (
          <div className="chat-search-asked-question">
            <span>You</span>
            <p>{ragQuestion}</p>
          </div>
        )}
        <div role="status" className="chat-search-evidence-summary">
          {aiLoading
            ? "Finding supporting passages and preparing a grounded answer..."
            : ragStatus === "NO_RELEVANT_EVIDENCE" && !ragMissExplanation && (
                <React.Fragment>
                  <h4>No evidence found</h4>
                  <p>
                    No relevant passages were found for this question in the
                    current search results.
                  </p>
                </React.Fragment>
              )}
        </div>
        {ragAnswerResponse && (
          <RagAnswer
            key={this.aiRequestGeneration}
            question={ragQuestion}
            response={ragAnswerResponse}
            evidence={ragEvidence}
            getDocument={this.getEvidenceDocument}
            onView={this.openAiDocument}
          />
        )}
        {aiError && this.visibleAiError() && (
          <p className="chat-search-ai-error" role="alert">
            {aiError}
          </p>
        )}
        {!aiLoading && ragMissExplanation && !ragAnswerResponse && (
          <RagMissCard
            explanation={ragMissExplanation}
            getDocument={this.getEvidenceDocument}
            onView={this.openAiDocument}
            onSearch={this.startSuggestedSearch}
            disabled={aiLoading}
          />
        )}
        {!aiLoading && ragStatus === "DEGRADED" && !ragAnswerResponse && !ragMissExplanation && (
          <p className="chat-search-ask-scope">
            I couldn't confirm an answer from these passages. Open a source
            below to read it in full.
          </p>
        )}
        {ragEvidence.length > 0 && (
          <div className="chat-search-evidence-summary">
            <h4>Supporting sources</h4>
            <p>
              {ragEvidence.length}{" "}
              {ragEvidence.length === 1 ? "passage" : "passages"} from the
              current search results
            </p>
          </div>
        )}
        <div className="chat-search-evidence-list" aria-busy={aiLoading}>
          {ragEvidence.map((item, index) => (
            <RagEvidenceCard
              key={`${this.aiRequestGeneration}-${index}`}
              evidence={item}
              {...this.getEvidenceDocument(item)}
              passageId={`chatSearchEvidencePassage-${index}`}
              pageCount={this.citedPageCount(item)}
              onView={this.openAiDocument}
            />
          ))}
        </div>
        {/* With a couldn't-find card the card says it kindly; the server's own note is not repeated under it. */}
        {ragCoverageWarning && !ragMissExplanation && (
          <p className="chat-search-coverage-note">
            {coverageMessage(ragCoverageWarning)}
          </p>
        )}
        {activeSearchSessionId && !ragNeedsNewSearch && !this.props.designLayout && (
          <SearchSessionReport
            key={`${this.aiContextKey()}:${activeSearchSessionId}`}
            searchSessionId={activeSearchSessionId}
            originalSearch={this.state.lastAiUserQuery}
            disabled={aiLoading || this.state.summaryLoading}
          />
        )}
        {!ragNeedsNewSearch && !this.props.designLayout && (
          <DocumentSummaries
            documents={this.state.aiDocuments}
            selected={this.state.summarySelected}
            loading={this.state.summaryLoading}
            response={this.state.summaryResponse}
            error={this.state.summaryError}
            disabled={aiLoading}
            sessionCount={searchSessionDocumentCount}
            onToggle={this.toggleSummarySelection}
            onSummarize={this.summarizeDocuments}
            onCancel={this.cancelAiRequest}
            onView={this.openAiDocument}
          />
        )}
      </section>
    );
  };

  getBlockLotSearchDescription = () => {
    const block = this.state.blockLotSearchedBlock;
    const lot = this.state.blockLotSearchedLot;

    if (block && lot) {
      return "Block " + block + " and Lot " + lot;
    }

    if (block) {
      return "Block " + block;
    }

    return "Lot " + lot;
  };

  getBlockLotResultSummary = () => {
    const count = this.state.blockLotResultCount;
    const searchDescription = this.getBlockLotSearchDescription();

    if (count === 0) {
      return "No documents were found for " + searchDescription + ".";
    }

    const documentLabel = count === 1 ? "document" : "documents";
    if (this.state.blockLotIsTruncated) {
      return (
        count +
        " matching " +
        documentLabel +
        " shown for " +
        searchDescription +
        "."
      );
    }

    return (
      count + " " + documentLabel + " found for " + searchDescription + "."
    );
  };

  renderBlockLotSearch = () => (
    <div
      id={"chatSearchBlockLotPanel"}
      className={"chat-search-block-lot-panel"}
      role={"tabpanel"}
      aria-labelledby={"chatSearchBlockLotTab"}
    >
      <form
        className={"chat-search-block-lot-form"}
        onSubmit={this.handleBlockLotSubmit}
      >
        <div className={"chat-search-block-lot-fields"}>
          <div className={"chat-search-block-lot-field"}>
            <label htmlFor={"chatSearchBlock"}>Block</label>
            <input
              id={"chatSearchBlock"}
              type={"text"}
              value={this.state.block}
              onChange={this.handleBlockChange}
              autoComplete={"off"}
            />
          </div>
          <div className={"chat-search-block-lot-field"}>
            <label htmlFor={"chatSearchLot"}>Lot</label>
            <input
              id={"chatSearchLot"}
              type={"text"}
              value={this.state.lot}
              onChange={this.handleLotChange}
              autoComplete={"off"}
            />
          </div>
          <button
            type={"submit"}
            className={"chat-search-block-lot-button"}
            disabled={this.state.blockLotLoading}
          >
            {this.state.blockLotLoading ? "Searching..." : "Search"}
          </button>
        </div>
        {this.state.blockLotError && (
          <div className={"chat-search-block-lot-error"} role={"alert"}>
            {this.state.blockLotError}
          </div>
        )}
      </form>

      <div
        className={
          this.state.blockLotSubmitted
            ? "chat-search-block-lot-results chat-search-block-lot-results-submitted"
            : "chat-search-block-lot-results"
        }
      >
        {this.state.blockLotSubmitted && (
          <React.Fragment>
            <div className={"chat-search-block-lot-summary"}>
              {this.getBlockLotResultSummary()}
            </div>
            {this.state.blockLotIsTruncated && (
              <div className={"chat-search-block-lot-warning"}>
                Showing the first {this.state.blockLotResultCount} matching
                documents for {this.getBlockLotSearchDescription()}. Refine the
                search values if needed.
              </div>
            )}
            <div className={"chat-search-block-lot-diagnostics"}>
              {this.state.blockLotTemplatesEvaluated} templates evaluated;{" "}
              {this.state.blockLotTemplatesSearched} templates searched.
            </div>
            {this.state.blockLotResults.length > 0 && (
              <div className={"chat-search-results"}>
                {this.state.blockLotResults.map((item, index) => (
                  <div
                    className={"chat-search-result-card"}
                    key={String(item.fileId) + "-" + index}
                  >
                    <button
                      type={"button"}
                      className={"chat-search-file-link"}
                      onClick={() =>
                        this.openViewer(item, { suppressViewerSearch: true })
                      }
                    >
                      {item.fileName || "Unknown file"}
                    </button>
                    <div className={"chat-search-result-meta"}>
                      <span>
                        <strong>Repository:</strong>{" "}
                        {item.repositoryName || "-"}
                      </span>
                      <span>
                        <strong>Template:</strong>{" "}
                        {this.formatTemplateName(item.templateName)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  );

  renderMessage = (message) => {
    if (message.type === "user") {
      return (
        <div
          className="chat-search-bubble chat-search-bubble-user"
          key={message.id}
        >
          {message.text}
        </div>
      );
    }

    if (message.type === "error") {
      return (
        <div
          className="chat-search-bubble chat-search-bubble-error"
          key={message.id}
        >
          {message.text}
        </div>
      );
    }

    return (
      <div
        className="chat-search-bubble chat-search-bubble-response"
        key={message.id}
      >
        <div className="chat-search-response-title">{message.text}</div>
        {message.results && message.results.length > 0 ? (
          <div className="chat-search-results">
            {message.results.map((item, index) => (
              <div
                className="chat-search-result-card"
                key={`${item.fileId || item.FileId}-${index}`}
              >
                <button
                  type="button"
                  className="chat-search-file-link"
                  onClick={() => this.openViewer(item)}
                >
                  {item.fileName || item.FileName || "Unknown file"}
                </button>
                <div className="chat-search-result-meta">
                  <span>
                    <strong>Repository:</strong>{" "}
                    {item.repositoryName || item.RepositoryName || "-"}
                  </span>
                  <span>
                    <strong>Template:</strong>{" "}
                    {this.formatTemplateName(
                      item.templateName || item.TemplateName || "",
                    )}
                  </span>
                  <span>
                    <strong>Score:</strong>{" "}
                    {item.textSearchScore ?? item.TextSearchScore ?? "-"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="chat-search-empty">No results found.</div>
        )}
      </div>
    );
  };

  render() {
    if (this.isEmbedded()) {
      return this.renderEmbedded();
    }
    return this.renderStandalone();
  }

  renderViewerPopup = (iframeId, title) => (
    <Popup
      contentStyle={{
        width: "95%",
        borderRadius: "15px",
        padding: "15px",
      }}
      open={this.state.viewerOpen}
      modal
      closeOnDocumentClick={false}
      onClose={this.closeViewer}
    >
      <div>
        {/* eslint-disable-next-line */}
        <a className="close" onClick={this.closeViewer}>
          &times;
        </a>
        <iframe
          id={iframeId}
          title={title}
          style={{ width: "100%", height: "90vh" }}
          src={config.ImageViewer}
        />
      </div>
    </Popup>
  );

  // ---- AI Assistant tab as designed (designLayout): the same state and actions, laid out as the boss's design shows.

  // Citation numbers as the answer shows them ([1], [2] in order of first citation), by "templateId:fileId".
  citedNumbers = () => {
    const numbers = new Map();
    const response = this.state.ragAnswerResponse;
    (response && Array.isArray(response.claims) ? response.claims : []).forEach((claim) =>
      (Array.isArray(claim.citations) ? claim.citations : []).forEach((citation) => {
        const key = `${citation.templateId}:${citation.fileId}`;
        if (!numbers.has(key)) numbers.set(key, numbers.size + 1);
      }),
    );
    return numbers;
  };

  formatGridDate = (value) => {
    // 1900-01-01 is SQL Server's empty date stored in some index fields, not a real date.
    if (String(value || "").slice(0, 10) === "1900-01-01") return "";
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
    return match ? `${match[2]}/${match[3]}/${match[1]}` : "";
  };

  // "Documents behind this answer": the session's records with Summarize selected and Generate report in the header,
  // then the summaries and the report.
  renderDesignDocuments = () => {
    const { activeSearchSessionId, ragNeedsNewSearch, aiLoading, summaryLoading, summarySelected } = this.state;
    const documents = Array.isArray(this.state.aiDocuments) ? this.state.aiDocuments : [];
    if (!activeSearchSessionId || ragNeedsNewSearch || documents.length === 0) return null;
    const distinct = [...new Map(documents.filter((d) => Number.isInteger(d.templateId) && Number.isInteger(d.fileId))
      .map((d) => [summaryDocumentKey(d), d])).values()];
    const cited = this.citedNumbers();
    const busy = aiLoading || summaryLoading;
    const count = this.state.searchSessionDocumentCount !== null ? this.state.searchSessionDocumentCount : documents.length;
    const showDates = distinct.some((d) => d.documentDate);
    const showPages = distinct.some((d) => d.pageCount);
    return (
      <section className="ds-docs" aria-label="Documents behind this answer">
        <div className="ds-docs-head">
          <h4>
            {`Documents behind this answer (${distinct.length})`}
            <span className="ds-docs-selected">{` · ${summarySelected.length} selected`}</span>
          </h4>
          <div className="ds-docs-actions">
            <button type="button" className="ds-btn-secondary" disabled={busy || summarySelected.length === 0}
              onClick={() => this.summarizeDocuments(summarySelected.length === 1 ? "SINGLE_DOCUMENT" : "SELECTED_DOCUMENTS",
                distinct.filter((d) => summarySelected.includes(summaryDocumentKey(d))))}>
              {`Summarize selected (${summarySelected.length} of 4)`}
            </button>
            <button type="button" className="ds-btn-primary" disabled={busy}
              onClick={() => this.setState((state) => ({ reportOpenRequest: (state.reportOpenRequest || 0) + 1 }))}>
              Generate report
            </button>
          </div>
        </div>
        <div className="chat-search-ai-table-wrapper">
          <table className="chat-search-ai-table ds-docs-table">
            <thead>
              <tr>
                <th>File Name</th>
                <th>Index Template</th>
                <th>Repository</th>
                {showDates && <th>Document Date</th>}
                <th>Cited as</th>
                {this.showsRedactionColumn() && <th>Redaction</th>}
                {showPages && <th>Page Count</th>}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {distinct.map((document) => {
                const number = cited.get(`${document.templateId}:${document.fileId}`);
                return (
                  <tr key={summaryDocumentKey(document)}>
                    <td>
                      {this.renderSummaryCheckbox(document)}
                      {document.fileName || `File ${document.fileId}`}
                    </td>
                    <td>{this.formatTemplateName(document.templateName)}</td>
                    <td>{document.repositoryName}</td>
                    {showDates && <td>{this.formatGridDate(document.documentDate)}</td>}
                    <td>{number ? `[${number}]` : ""}</td>
                    {this.showsRedactionColumn() && <td>{this.redactionLabel(document)}</td>}
                    {showPages && <td>{document.pageCount || ""}</td>}
                    <td>
                      <button type="button" onClick={() => this.openAiDocument(document)}>View</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="ds-docs-note">
          {`Summaries and reports use ${count === 1 ? "this record" : `these ${count} records`} only.`}
          {this.state.sessionIsLaterPage ? ' These are the results of "Check more results" only.' : ""}
        </p>
        <DocumentSummaries
          documents={documents}
          selected={summarySelected}
          loading={summaryLoading}
          response={this.state.summaryResponse}
          error={this.state.summaryError}
          disabled={aiLoading}
          sessionCount={this.state.searchSessionDocumentCount}
          onToggle={this.toggleSummarySelection}
          onSummarize={this.summarizeDocuments}
          onCancel={this.cancelAiRequest}
          onView={this.openAiDocument}
          hideToolbar
        />
        <SearchSessionReport
          key={`${this.aiContextKey()}:${activeSearchSessionId}`}
          searchSessionId={activeSearchSessionId}
          originalSearch={this.state.lastAiUserQuery}
          disabled={aiLoading || summaryLoading}
          openRequest={this.state.reportOpenRequest}
          hideOpenButton
        />
      </section>
    );
  };

  // The follow-up bar at the bottom (design): the one suggestion that fits any result set, the follow-up box, New search.
  renderDesignFollowUpBar = () => {
    const { aiLoading, ragNeedsNewSearch } = this.state;
    const count = this.state.searchSessionDocumentCount !== null && this.state.searchSessionDocumentCount !== undefined
      ? this.state.searchSessionDocumentCount
      : Array.isArray(this.state.aiDocuments) && this.state.aiDocuments.length ? this.state.aiDocuments.length : null;
    return (
      <div className="ds-followup-bar">
        {!ragNeedsNewSearch && (
          <div className="ds-followup-chips">
            <span>
              {count === 1 ? "Ask a follow-up about this record:" : count !== null ? `Ask a follow-up about these ${count} records:` : "Ask a follow-up about these results:"}
            </span>
            <button type="button" className="chat-search-ai-action" disabled={aiLoading}
              onClick={() => this.searchWithAiNaturalLanguage("What other details are mentioned?")}>
              What other details are mentioned?
            </button>
          </div>
        )}
        {this.renderSessionBar()}
      </div>
    );
  };

  renderAssistantDesign = () => {
    const hasSession = this.state.activeSearchSessionId || this.state.ragNeedsNewSearch;
    const hasConversation = Array.isArray(this.state.aiChatMessages) && this.state.aiChatMessages.length > 0;
    return (
      <div className="chat-search-embedded chat-search-embedded-assistant ds-assistant" data-testid="chat-search-embedded">
        <div id="chatSearchDocumentPanel" className="chat-search-body chat-search-body-embedded" role="region" aria-label="AI Assistant">
          <div className="chat-search-messages" ref={this.chatHistoryRef}>
            {!hasConversation && !this.state.aiLoading && (
              <div className="chat-search-empty chat-search-empty-embedded">
                <h3>Ask a question about your documents</h3>
                <p>Type your question on the left. Answers are grounded in the records in scope, with citations you can open in the viewer.</p>
              </div>
            )}
            {this.renderAiSearchConversation()}
            {this.renderAskResults()}
            {this.renderDesignDocuments()}
            {!this.state.activeSearchSessionId && !this.state.ragNeedsNewSearch && this.visibleAiError() && (
              <p className="chat-search-ai-error" role="alert">{this.visibleAiError()}</p>
            )}
            <div ref={(el) => (this.messagesEnd = el)} />
          </div>
          {hasSession && this.renderDesignFollowUpBar()}
        </div>
        {typeof this.props.onViewDocument !== "function" &&
          this.renderViewerPopup("chatSearchImageViewer", "Document Chat Search Viewer")}
      </div>
    );
  };

  // Inline inside the Search screen: no overlay, header, mode tabs or repository picker; the screen supplies the
  // scope and (in semantic mode) the query box, and this component keeps every AI behaviour.
  renderEmbedded = () => {
    const mode = this.getEmbeddedMode();
    const isAssistant = mode === CHAT_SEARCH_MODE_ASSISTANT;
    if (isAssistant && this.props.designLayout) return this.renderAssistantDesign();
    const hasSession =
      this.state.activeSearchSessionId || this.state.ragNeedsNewSearch;
    const showComposer = this.props.hideComposer !== true;
    const hasConversation =
      Array.isArray(this.state.aiChatMessages) &&
      this.state.aiChatMessages.length > 0;

    return (
      <div
        className={`chat-search-embedded chat-search-embedded-${mode}`}
        data-testid="chat-search-embedded"
      >
        <div
          id="chatSearchDocumentPanel"
          className="chat-search-body chat-search-body-embedded"
          role="region"
          aria-label={isAssistant ? "AI Assistant" : "Semantic search results"}
        >
          <div className="chat-search-messages" ref={this.chatHistoryRef}>
            {!hasConversation && !this.state.aiLoading && (
              <div className="chat-search-empty chat-search-empty-embedded">
                <h3>
                  {isAssistant
                    ? "Ask a question about your documents"
                    : "Describe what you are looking for"}
                </h3>
                <p>
                  {isAssistant
                    ? "Answers are grounded in the documents in scope, with citations you can open in the viewer."
                    : "DocumentSync AI reads index values and document text, so the wording does not have to match exactly."}
                </p>
              </div>
            )}
            {this.renderAiSearchConversation()}
            {isAssistant && this.renderAskResults()}
            {!this.state.activeSearchSessionId &&
              !this.state.ragNeedsNewSearch &&
              this.state.aiError && (
                <p className="chat-search-ai-error" role="alert">
                  {this.state.aiError}
                </p>
              )}
            <div ref={(el) => (this.messagesEnd = el)} />
          </div>
          {showComposer &&
            (isAssistant && hasSession
              ? this.renderSessionBar()
              : this.renderSearchComposer(true))}
        </div>
        {typeof this.props.onViewDocument !== "function" &&
          this.renderViewerPopup(
            "chatSearchImageViewer",
            "Document Chat Search Viewer",
          )}
      </div>
    );
  };

  renderStandalone() {
    const canUseAiNaturalLanguage = this.canUseAiNaturalLanguageSearch();
    const isAiNaturalLanguageSearch =
      canUseAiNaturalLanguage && this.state.isAiNaturalLanguageSearch;

    return (
      <div className="chat-search-overlay" role="dialog" aria-modal="true">
        <div
          className={
            this.state.activeSearchTab === "blockLot"
              ? "chat-search-modal chat-search-modal-block-lot"
              : "chat-search-modal"
          }
        >
          <div className="chat-search-header">
            <div className="chat-search-header-title">
              <div className="chat-search-title">Global Search</div>
              <div className="chat-search-subtitle">
                {this.state.activeSearchTab === "document"
                  ? "Search across repositories using text or natural language"
                  : "Find documents using exact Block and Lot values"}
              </div>
            </div>
            <button
              type="button"
              className="chat-search-close"
              onClick={this.handleChatSearchClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="chat-search-tabs" role="tablist">
            <button
              id="chatSearchDocumentTab"
              type="button"
              role="tab"
              aria-selected={this.state.activeSearchTab === "document"}
              aria-controls="chatSearchDocumentPanel"
              className={
                this.state.activeSearchTab === "document"
                  ? "chat-search-tab chat-search-tab-active"
                  : "chat-search-tab"
              }
              onClick={() => this.handleSearchTabChange("document")}
            >
              Document Search
            </button>
            <button
              id="chatSearchBlockLotTab"
              type="button"
              role="tab"
              aria-selected={this.state.activeSearchTab === "blockLot"}
              aria-controls="chatSearchBlockLotPanel"
              className={
                this.state.activeSearchTab === "blockLot"
                  ? "chat-search-tab chat-search-tab-active"
                  : "chat-search-tab"
              }
              onClick={() => this.handleSearchTabChange("blockLot")}
            >
              Block &amp; Lot Search
            </button>
          </div>

          <div className="chat-search-repository-bar">
            <label htmlFor="chatSearchRepository">Repository</label>
            <select
              id="chatSearchRepository"
              value={
                this.state.allRepositories
                  ? "all"
                  : this.state.selectedRepositoryId || "all"
              }
              onChange={this.handleRepositoryChange}
            >
              <option value="all">All repositories</option>
              {this.state.repositories.map((repository) => (
                <option
                  key={
                    repository.repositoryId ||
                    repository.id ||
                    this.getRepositoryLabel(repository)
                  }
                  value={repository.repositoryId || repository.id}
                >
                  {this.getRepositoryLabel(repository)}
                </option>
              ))}
            </select>
            {this.state.activeSearchTab === "document" &&
              canUseAiNaturalLanguage && (
                <label className="chat-search-ai-toggle">
                  <input
                    type="checkbox"
                    checked={isAiNaturalLanguageSearch}
                    onChange={this.handleAiNaturalLanguageSearchToggle}
                    aria-label="Natural Language Search with AI"
                  />
                  <span>Natural Language Search with AI</span>
                </label>
              )}
          </div>

          {this.state.activeSearchTab === "document" ? (
            <React.Fragment>
              <div
                id="chatSearchDocumentPanel"
                className="chat-search-body"
                role="tabpanel"
                aria-labelledby="chatSearchDocumentTab"
              >
                <div className="chat-search-messages" ref={this.chatHistoryRef}>
                  {!isAiNaturalLanguageSearch &&
                    this.state.messages.length === 0 && (
                      <div className="chat-search-empty">
                        <h3>Search across your documents</h3>
                        <p className="chat-search-examples">
                          Type a file name, a record number, a name or words
                          that appear in the documents.
                        </p>
                      </div>
                    )}
                  {isAiNaturalLanguageSearch
                    ? this.renderAiSearchConversation()
                    : this.state.messages.map(this.renderMessage)}
                  {isAiNaturalLanguageSearch && this.renderAskResults()}
                  {isAiNaturalLanguageSearch &&
                    !this.state.activeSearchSessionId &&
                    !this.state.ragNeedsNewSearch &&
                    this.state.aiError && (
                      <p className="chat-search-ai-error" role="alert">
                        {this.state.aiError}
                      </p>
                    )}
                  <div ref={(el) => (this.messagesEnd = el)} />
                </div>
                {isAiNaturalLanguageSearch &&
                (this.state.activeSearchSessionId ||
                  this.state.ragNeedsNewSearch)
                  ? this.renderSessionBar()
                  : this.renderSearchComposer(isAiNaturalLanguageSearch)}
              </div>
            </React.Fragment>
          ) : (
            this.renderBlockLotSearch()
          )}

          {this.renderViewerPopup(
            "chatSearchImageViewer",
            "Document Chat Search Viewer",
          )}
        </div>
      </div>
    );
  }
}

export default ChatSearch;
